import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import {
  COMPANY_ADDRESS_PAIRS,
  COMPANY_UNPAIRED_SHIPPING_KEYS,
  SAME_AS_BILLING_LABEL,
} from '@/lib/address-pairs';
import { ApiError, api } from '@/lib/api';
import { joinList } from '@/lib/format';
import {
  describeNewCompanyApproval,
  isResendable,
  newCompanyHeadline,
  resendDenialFor,
} from '@/lib/company-approval';
import { BILLING_NAME_LABEL, COMPANY_NAME_LABEL } from '@/lib/company-names';
import { ResendApprovalControl } from '@/components/companies/ResendApprovalControl';
import type {
  Company,
  CompanyCreateResult,
  CompanyInput,
  CompanyStageStatus,
  Contact,
  ContactGroup,
  ContactInput,
  Country,
  MasterRef,
  ReferenceRecord,
  SimilarCompany,
  UserSummary,
} from '@/lib/types';
import { CompanyContactsDraft } from '@/components/companies/CompanyContactsDraft';
import {
  applyDraftContactPatch,
  isBlankDraftContact,
  newDraftContact,
  type DraftContact,
  type DraftContactErrors,
  type DraftContactPatch,
} from '@/components/companies/company-contacts-draft';
import { CONTACT_NAME_LABELS } from '@/lib/contact-name';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import { USER_GROUP_SLUGS, narrowingNote, outsideGroupNote } from '@/lib/user-groups';
import { companySourceOptions, useCompanySources } from '@/lib/use-company-sources';
import { companyTypeOptions, useCompanyTypes } from '@/lib/use-company-types';
import { useCompanyStages } from '@/lib/use-company-stages';
import {
  filterStatusesForStage,
  useCompanyStageStatuses,
} from '@/lib/use-company-stage-statuses';
import {
  FOLLOW_UP_QUICK_PICKS,
  addMonthsIso,
  needsFollowUpDialog,
  nextCycleFollowUpDate,
  nextDayIso,
  todayUtcIsoDate,
} from '@/lib/stage-status-follow-up';
import { withStoredOption } from '@/lib/use-reference-options';
import { useXero } from '@/lib/xero-context';
import { toLineAmountType, useXeroTaxRates } from '@/lib/xero-tax-rates';
import { cx } from '@/lib/cx';
import {
  Badge,
  Button,
  Combobox,
  EmptyState,
  Field,
  Input,
  Modal,
  Radio,
  Skeleton,
  Textarea,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { XeroSalesDefaultsFields } from '@/components/xero/XeroSalesDefaultsFields';

export interface CompanyFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  company?: Company;
  /** Called with the saved record so the caller can refresh in place. */
  onSaved: (company: Company) => void;
  /**
   * Starting values for a NEW company — an enquiry's institution name and
   * country. ⚠️ **CREATE ONLY**: ignored when `company` is given, because an
   * edit starts from the record. They seed the form and stay editable; they are
   * never the diff baseline, so a create still sends exactly what is on screen.
   */
  prefill?: CompanyFormPrefill;
  /**
   * CREATE ONLY: the label of the one footer button on the confirmation the
   * window turns into after a create (owner, 2026-10-16), e.g. "Continue to
   * deal". Defaults to "Close".
   *
   * ⚠️ On a create, `onSaved` fires exactly once — when that button is pressed
   * or the window is closed (X / Escape) — never straight after the POST. It
   * receives the freshest copy (the primary-contact read-back when one was set),
   * carrying `approvalRequest` from the `201`. `onClose` is called right after.
   */
  createdContinueLabel?: string;
}

export interface CompanyFormPrefill {
  name?: string;
  /** A country of record. `null` / absent leaves the picker blank. */
  countryId?: number | null;
  /** A website, e.g. from a SEED platform record in the Company mapper (2026-10-25). */
  website?: string | null;
}

/** The create form's opening values: the empty record, with any prefill laid over it. */
function withPrefill(base: FormValues, prefill: CompanyFormPrefill | undefined): FormValues {
  if (!prefill) return base;
  const next = { ...base };
  if (prefill.name !== undefined) {
    next.name = prefill.name;
    // The Billing name follows the Company name on a create until the user
    // edits it (owner 2026-10-25), so a seeded name seeds both.
    next.billingName = prefill.name;
  }
  if (typeof prefill.countryId === 'number') next.countryId = String(prefill.countryId);
  if (typeof prefill.website === 'string' && prefill.website.trim() !== '') {
    next.website = prefill.website.trim();
  }
  return next;
}

/*
 * Server-enforced max lengths, mirrored so the control stops the user at the
 * same place the API would. Deliberately the *only* client rule on
 * phone / website / linkedin: the API validates those by length alone, and a
 * stricter format rule here would reject values it happily accepts.
 */
const NULLABLE_TEXT_FIELDS = {
  /*
   * *SUPERSEDED 2026-10-25: `billingName` lived here because blank cleared it.*
   * It is now REQUIRED (owner 2026-10-25: the Billing name is always the Xero
   * Contact name) and sits beside `name` in `MAX_LENGTHS` below.
   */
  email: 255,
  secondaryEmail: 255,
  mobile: 32,
  alternateMobile: 32,
  website: 255,
  linkedin: 255,
  billingStreet: 255,
  billingCity: 120,
  billingState: 120,
  billingCode: 32,
  /*
   * ⚠️ **`billingCountry` IS DELIBERATELY ABSENT, and its absence is the
   * guard.** Owner's decision, 2026-08-31: a company's country is the foreign
   * key (`countryId`, see `CountryField`) and the free-text billing country is
   * no longer collected. Keeping the key here would leave it in `FormValues`,
   * so the PATCH branch would send `null` for a box that no longer renders —
   * and an explicit `null` CLEARS, so the 111 companies that hold a value would
   * lose it the first time anybody edited an unrelated field, with a 200 and
   * nothing to restore from. Now no payload this form can build is able to
   * express the key and `tsc` refuses any call site that tries. Same pattern as
   * `ownerId` and `primaryContactId` below.
   *
   * ⚠️ `shippingCountry` STAYS — shipping keeps its free-text country, and it
   * has no foreign key of its own.
   */
  shippingStreet: 255,
  shippingCity: 120,
  shippingState: 120,
  shippingCode: 32,
  shippingCountry: 120,
  description: 20000,
} as const;

/*
 * Everything in NULLABLE_TEXT_FIELDS clears to null. `name`, `billingName` and
 * `layout` are never null server-side, so they are kept apart and handled by
 * hand below.
 *
 * `name` is the Company name (display only; never pushed to Xero, not unique).
 * `billingName` is the Billing name = the Xero Contact name: required, unique
 * (409 `COMPANY_BILLING_NAME_TAKEN`), and a blank PATCH is 422
 * `BILLING_NAME_REQUIRED`. Both are routed to its control in `reportSaveError`.
 */
const MAX_LENGTHS = { name: 255, billingName: 255, layout: 60, ...NULLABLE_TEXT_FIELDS } as const;

type TextKey = keyof typeof MAX_LENGTHS;
/**
 * ⚠️ **`ownerId` is deliberately ABSENT — the owner is deprecated on a company
 * (owner's instruction, 2026-08-30), and this form was its only writer in the
 * app.**
 *
 * Absent from the union rather than merely unrendered, which is the whole point:
 * a key left reachable here stays in `FormValues`, so `changedStaffKeys` can
 * still reach it and the PATCH branch below sends `null` for a cleared staff
 * key — an explicit `null` CLEARS the column. A company that already had an
 * owner would lose it the first time anybody edited its billing address, with a
 * `200` and nothing to restore from. Now no payload this form can build is able
 * to express the key, and `tsc` refuses any call site that tries. Same pattern
 * as `ProductFormModal`'s Xero keys and `DealFormModal`'s `projectId`.
 *
 * The COLUMN is untouched and 148 companies may still hold a value: this
 * deprecates the write, it does not delete the data. `?ownerId=` remains a real
 * `/companies` filter param (no column drives it today) and is a READ.
 */
type StaffKey = 'accountLeadId' | 'clientSuccessManagerId';
/**
 * ⚠️ **`primaryContactId` is deliberately ABSENT, and its absence is the
 * whole control: the server REFUSES it on CREATE outright.** A company has no
 * contacts until it exists, so there is nothing legitimate to name here — a
 * "Main contact" picker on this form could only ever offer an empty list or
 * somebody else's contact, and either one is a 422 the person cannot act on.
 *
 * This modal builds ONE `FormValues` for both modes, so a key added "for edit
 * only" is still reachable on create. Keeping it out of the union means no
 * payload this form can build is able to express it and `tsc` refuses any call
 * site that tries — the structural version of the guard rather than one
 * somebody has to remember. Same pattern as `ownerId` above.
 *
 * **Editing it lives on `CompanyDetailPage`** (`primaryContactField`), and
 * since 2026-10-15 ALSO on this form's EDIT mode, as the Primary radio in the
 * Contacts section. That write is built by hand in `buildPayload`'s edit branch
 * (and `setPrimaryContact` for a new row) and still never enters `FormValues`.
 * *SUPERSEDED: "That page is the field's only writer in the app."*
 *
 * The other four refusals are all `422`s carrying `path: 'primaryContactId'`
 * and NO error `code`, so the server's own `message` is the whole payload: the
 * contact does not exist, belongs to a different company, is half-named (one of
 * first / last recorded and not the other), or has opted out of marketing
 * email.
 */
/**
 * Reference-master ids, kept as their own group rather than folded into
 * `STAFF_KEYS`: a stage is a picklist row, not a person, and the two carry
 * different traps (see `StageField`).
 *
 * ⚠️ **`countryId` rides here deliberately rather than getting a branch of its
 * own.** Both payload loops below already map `'' → null` and otherwise
 * `Number(...)` for this group, both diffs already compare it, and
 * `KNOWN_WRITE_KEYS` is derived from `toFormValues()` — so adding the key in one
 * place wires the control, the diff, the create branch, the patch branch and the
 * 422 routing at once. A bespoke branch would have been five more places to keep
 * in step, each silent when it drifted.
 *
 * ⚠️ **`countryId` is the company's ONLY country now** — *SUPERSEDED
 * 2026-08-31: "`countryId` is NOT `billingCountry`. The latter is free text in
 * the Address section and stays exactly as it was."* The free-text billing
 * country is no longer collected on a company (owner's decision), so this
 * foreign key is the single source of truth the server derives the billing
 * region, the currency and the Xero defaults from. See `CountryField`, and
 * `MAX_LENGTHS` for why the old key is absent rather than merely unrendered.
 *
 * ⚠️ **`sourceId` rides here for exactly the reason `countryId` does, and that
 * is the WHOLE wiring.** `changedPicklistKeys`, both branches of
 * `buildPayload` and `KNOWN_WRITE_KEYS` are all derived from this union and
 * `PICKLIST_KEYS`, so adding the key in those two places wires the control, the
 * diff, the create branch, the patch branch and the 422 routing at once —
 * verified by reading all four rather than assumed. A bespoke branch would have
 * been four more places to keep in step, each silent when it drifted.
 *
 * ⚠️ **`source: null` is the ORDINARY state, on every company that exists
 * today** — the column is new and nothing was backfilled — so '' maps to `null`
 * here exactly as it does for `stageId`, and never to the first master row. A
 * picker that quietly preselected "Organic" would put a claim about where a
 * school came from on 413 records that nobody made.
 *
 * ⚠️ **`companyTypeId` rides here on exactly the same terms, and it is NOT a
 * second spelling of `sourceId` beside it.** A source is where the account
 * arrived FROM; a type is what the organisation IS. They are adjacent in the
 * union and adjacent on screen because one person answers both in one pass —
 * which is also why the two labels must stay visibly different words.
 *
 * ⚠️ **`companyType: null` is the ORDINARY state, on every company that exists
 * today** — the column is new and nothing was backfilled — so '' maps to `null`
 * here exactly as it does for `sourceId`, and ⛔ **never to `Institution`**. It
 * is the seeded first row and the overwhelmingly likely answer, which is what
 * makes preselecting it so tempting: it would put a classification nobody made
 * on every existing company and on every new one until somebody moved the
 * picker.
 */
/*
 * `stageStatusId` rides here on the same terms as `stageId` (owner 2026-10-23):
 * '' is null, it is sent only when moved on an edit, and omitted when blank on a
 * create. Changing Stage clears it in `setField`, because a status belongs to
 * exactly one stage.
 */
type PicklistKey = 'stageId' | 'stageStatusId' | 'countryId' | 'sourceId' | 'companyTypeId';
/**
 * Xero's **Sales defaults** for this customer — the two writable ones.
 *
 * ⚠️ **Neither changes how EMERGE calculates anything.** EMERGE's line
 * arithmetic is tax-EXCLUSIVE and has no inclusive mode at all, so a company
 * can read `INCLUSIVE` here while every figure this app shows is still worked
 * out exclusively. Every label and hint below therefore says what **Xero**
 * should default to, and never how something is calculated — a UI implying the
 * latter would have the two systems print different totals for one sale with
 * nothing flagging it.
 *
 * `xeroSalesTaxName` is deliberately absent: it is a read-only cached label the
 * server derives from the code and ignores if sent. Keeping it out of
 * `FormValues` means the diff can never reach it, which is the structural
 * version of the guard rather than one somebody has to remember.
 */
type XeroKey = 'xeroSalesLineAmountType' | 'xeroSalesTaxType';

const TEXT_KEYS = Object.keys(MAX_LENGTHS) as TextKey[];
const STAFF_KEYS: readonly StaffKey[] = ['accountLeadId', 'clientSuccessManagerId'];

/**
 * The form-only value meaning **nobody leads this account**, as opposed to a
 * blank picker, which on a CREATE means *let the server decide* and is answered
 * by making the creator the lead.
 *
 * ⛔ **IT NEVER REACHES THE WIRE.** `buildPayload` is the ONE place it is
 * translated, to an explicit `null`, and there is no second reader — the same
 * discipline the backend applies to `f_revenue_targets`' `''` currency
 * sentinel, where the rule is that a sentinel escaping its boundary becomes a
 * second representation of the same fact that every later comparison has to
 * know about.
 *
 * ⚠️ **`Number()` OF IT IS `NaN`, AND THAT IS THE FAILURE TO GUARD AGAINST** —
 * the staff loops coerce with `Number()`, so a path that meets this and does
 * not test for it first sends `NaN` rather than refusing. It is deliberately
 * not a numeric-looking string: nothing can mistake it for an id in a log, and
 * `d_users.id` is a positive integer, so it can never collide with one.
 */
const NO_ACCOUNT_LEAD = '__none';

/**
 * What the blank row says on a CREATE — where it does not mean "nobody".
 *
 * ⚠️ **The NAME is in the label rather than in a hint because the empty row
 * cannot carry one**: `Combobox` builds it as `{value: '', label}` and there is
 * no hint slot (`Combobox.tsx:211`). It earns its place twice over — a
 * NON-MEMBER never sees their own name anywhere else in this control, because
 * the list is narrowed to the group and they are not in it; and during a
 * support session the acting identity is the TARGET, not the operator reading
 * the screen, so a bare "You" would name the wrong person to the one person
 * who could not tell.
 */
function creatorLeadLabel(user: { name: string; email: string } | null): string {
  if (!user) return 'You';
  const who = user.name.trim() || user.email.trim();
  return who === '' ? 'You' : `You — ${who}`;
}

/**
 * The blank row wherever it genuinely means nobody — which is every mount of
 * `StaffField` except the Account lead on a CREATE. One copy, named, so the
 * default and the edit call site cannot drift into two spellings of one word.
 */
const UNASSIGNED_LABEL = 'Unassigned';

/**
 * The explicit "nobody" choice, module-level so its identity is STABLE across
 * renders — `StaffField`'s options are memoised on it, and an object literal at
 * the call site would rebuild that list every render.
 *
 * ⚠️ **"No account lead", not "None" or a second "Unassigned".** It sits one row
 * under a blank that on this path means *you*, so the two must not read alike:
 * the whole defect being fixed is two different instructions wearing one word.
 * It names the field because the row is only ever offered on that field.
 */
const NO_ACCOUNT_LEAD_OPTION: ComboboxOption = {
  value: NO_ACCOUNT_LEAD,
  label: 'No account lead',
};
const PICKLIST_KEYS: readonly PicklistKey[] = [
  'stageId',
  'stageStatusId',
  'countryId',
  'sourceId',
  'companyTypeId',
];
const XERO_KEYS: readonly XeroKey[] = ['xeroSalesLineAmountType', 'xeroSalesTaxType'];

/** Every value is a string: '' is "blank", which maps to null on the wire. */
type FormValues = Record<TextKey | StaffKey | PicklistKey | XeroKey, string>;

/**
 * The COLLAPSIBLE sections, and only those.
 *
 * ⚠️ **`'contacts'` is deliberately NOT a member.** The Contacts block is
 * rendered permanently open (`collapsible={false}`), so it has no disclosure
 * state to store, nothing to toggle and no fold an error could hide behind.
 * Adding it back here would put a key in `openSections` that nothing reads and
 * an entry in `SECTION_FIELDS` that can never match — see the block itself.
 */
type SectionId = 'contactDetails' | 'address' | 'more' | 'xero' | 'notes';

/*
 * Which request keys live behind each disclosure. Server `details[].path`
 * matches these exactly, so a failed save can find and open the section holding
 * the error — an error the user cannot see is worse than no error at all.
 *
 * ⚠️ **The unlabelled TOP GRID has no entry here, and that is correct rather
 * than an oversight.** `name`, `stageId`, `countryId`, `sourceId`,
 * `accountLeadId` and `clientSuccessManagerId` are all absent: nothing folds
 * them away, so there is no disclosure for `revealSectionsWithErrors` to open
 * and the control a 422 names is already on screen. Routability is a separate
 * question and is answered by `KNOWN_WRITE_KEYS`, which derives from
 * `toFormValues()` and therefore already covers every one of them.
 *
 * ⛔ **Do not "fix" a top-grid key by filing it under the nearest section.** The
 * entry would match and open a fold that does not contain the control, sending
 * the reader to the wrong half of the form while the real error sits above it.
 */
const SECTION_FIELDS: Record<SectionId, readonly string[]> = {
  /*
   * ⚠️ **All SIX of the company's own contact fields, and the three that moved
   * here left `more` in the same edit.** `email`, `mobile` and `website` were
   * loose in the unlabelled top grid while `secondaryEmail`, `alternateMobile`
   * and `linkedin` sat behind "More details", so one account's contact details
   * were split across two folds and a person editing them had to know which
   * half lived where.
   *
   * ⚠️ **This entry is the whole reason the move is safe.** `email` is
   * validated client-side AND is a real 422 path, and it now lives behind a
   * CLOSED disclosure — so without the key listed here a refusal would mark a
   * control nobody can see, and `FormErrorSummary` would say "1 field needs
   * attention, highlighted below" with nothing highlighted. Moving a control
   * behind a fold without moving its key is a silent defect, not a layout
   * change.
   */
  contactDetails: ['email', 'mobile', 'website', 'secondaryEmail', 'alternateMobile', 'linkedin'],
  /*
   * ⚠️ **BOTH lists, not just the pairs.** `COMPANY_ADDRESS_PAIRS` drops the
   * country pair (a company's country is the `countryId` foreign key now), but
   * `shippingCountry` survives with no billing half — so driving this off the
   * pairs alone would leave a real 422 path unlisted, and a refusal naming it
   * would mark a control behind a CLOSED disclosure that never opens.
   */
  address: [
    ...COMPANY_ADDRESS_PAIRS.flatMap(([billing, shipping]) => [billing, shipping]),
    ...COMPANY_UNPAIRED_SHIPPING_KEYS,
  ],
  /*
   * ⚠️ Down to `layout` alone — the other three moved to `contactDetails`
   * above. Kept as a section rather than folded away because `layout` is a real
   * writable column with a real 422 path, and it has nowhere else to live.
   */
  more: ['layout'],
  /*
   * Both Xero 422s — an unknown tax code, and a disconnected tenant — carry
   * `details[].path` of `xeroSalesTaxType`, so they bind to the control and
   * force this section open rather than rendering as a form-level banner about
   * a field nobody can see.
   */
  xero: ['xeroSalesLineAmountType', 'xeroSalesTaxType'],
  notes: ['description'],
};

const SECTION_IDS = Object.keys(SECTION_FIELDS) as SectionId[];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The record as the form sees it. Also the baseline every diff is taken against. */
function toFormValues(company?: Company): FormValues {
  const values = {} as FormValues;
  for (const key of TEXT_KEYS) values[key] = company?.[key] ?? '';
  values.accountLeadId = company?.accountLead ? String(company.accountLead.id) : '';
  values.clientSuccessManagerId = company?.clientSuccessManager
    ? String(company.clientSuccessManager.id)
    : '';
  /*
   * `stage: null` is a real state — "not yet classified" — and it is what a new
   * company starts as. '' therefore means null here exactly as it does for the
   * staff pickers, and never "Lead".
   */
  values.stageId = company?.stage ? String(company.stage.id) : '';
  values.stageStatusId = company?.stageStatus ? String(company.stageStatus.id) : '';
  /*
   * ⚠️ The ID ONLY, never the embedded label. `CompanyCountryRef`'s field set is
   * the one part of this contract taken from a description rather than measured
   * against a live response, so a renderer reaching for its name here would
   * compile and print `undefined` — the `LineCity` trap. `CountryField` matches
   * this id against the fetched master and takes the label from there, which
   * cannot be wrong about a spelling.
   *
   * `null` is the ordinary state: `country_id` is new and nothing was
   * backfilled, so every company that predates it reads '' here.
   */
  values.countryId = company?.country ? String(company.country.id) : '';
  /*
   * ⚠️ The ID ONLY, like the two above — the label comes from the master, which
   * cannot be stale about a spelling.
   *
   * `null` is the ordinary state rather than an edge: the column is new,
   * nothing was backfilled, and blank stays a perfectly good answer for a
   * school nobody recorded a source for. So '' means null here, and clearing
   * the picker is a real choice rather than a gap to be filled in.
   */
  values.sourceId = company?.source ? String(company.source.id) : '';
  /*
   * ⚠️ The ID ONLY again, and seeded HERE rather than anywhere else — which is
   * what gives `KNOWN_WRITE_KEYS` its entry for free, since that list is
   * `Object.keys(toFormValues())`. A 422 naming `companyTypeId` is therefore
   * routable to this control without a second list to keep in step.
   *
   * ⛔ **'' means `null`, and NEVER the first master row.** `Institution` is
   * seeded first and is what most accounts are, so a preselect would look
   * harmless and would classify every unclassified company on its next save.
   */
  values.companyTypeId = company?.companyType ? String(company.companyType.id) : '';
  /*
   * `null` is "use organisation settings", which is a real answer rather than a
   * missing one — so it maps to '' here exactly as the nullable pickers do, and
   * the controls label '' as that sentence instead of leaving it blank.
   */
  values.xeroSalesLineAmountType = company?.xeroSalesLineAmountType ?? '';
  values.xeroSalesTaxType = company?.xeroSalesTaxType ?? '';
  return values;
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS**, which is what a 422's `path` names;
 * `toFormValues()` is keyed on exactly those, so deriving the list from it
 * keeps it from drifting away from the controls. `xeroSalesTaxName` is
 * correctly absent — it is not in `FormValues`, because it is read-only.
 *
 * Anything outside the list goes to the form-level banner, because a path can
 * be non-empty and still bind to nothing — the worse case, since it looks
 * bindable: it is routed to an input that does not exist, vanishes from the
 * banner, and still inflates the "fields need attention" count. That count is
 * the one this file must not get wrong: half these controls live behind a
 * closed disclosure, so "highlighted below" is already asking for trust.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

/*
 * The stage-status follow-up inputs (owner 2026-10-23). Kept OUT of
 * `FormValues` on purpose: they are sent only when the status (or the date)
 * moves, never by the generic diff loops, so they are routed by hand here.
 */
const FOLLOW_UP_WRITE_KEYS: readonly string[] = ['followUpOn', 'stageStatusReason'];
const ROUTABLE_WRITE_KEYS: readonly string[] = [...KNOWN_WRITE_KEYS, ...FOLLOW_UP_WRITE_KEYS];
const STAGE_STATUS_REASON_MAX = 1000;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** 422 codes the server binds to the follow-up date, matched by code. */
const FOLLOW_UP_DATE_CODES: ReadonlySet<string> = new Set([
  'FOLLOW_UP_DATE_REQUIRED',
  'FOLLOW_UP_DATE_INVALID',
  'FOLLOW_UP_DATE_NOT_ALLOWED',
]);

/**
 * How many look-alikes the name field will show at once.
 *
 * Small on purpose. This is an aside beside a field somebody is still typing
 * into, not a search result: a long list pushes the rest of the form down on
 * every keystroke, and past a handful nobody reads it anyway. The server
 * ranks, so the ones worth seeing are the ones at the top.
 */
const SIMILAR_LIMIT = 5;

/**
 * A match's secondary label, or `null` meaning **say nothing**.
 *
 * `shortName`, `stage` and `city` are each independently nullable, and `''` is
 * reachable on any of them. Both absences must read as "not recorded" rather
 * than printing an empty segment between two separators — a stray `` · `` is
 * indistinguishable from a value that failed to render.
 */
function matchLabel(value: unknown): string | null {
  /*
   * ⚠️ `typeof`, NOT `value?.trim()`, AND THE PARAMETER IS `unknown` ON PURPOSE.
   * This was `(value: string | null)` and it threw `e?.trim is not a function`
   * in production, unmounting the entire app, because `SimilarCompany.stage`
   * was declared here as a string and arrives as `{id, name}`. That specific
   * bug is fixed at both ends — the type is corrected and the call site reads
   * `stage?.name` — so this guard is NOT the repair and must not be read as one.
   *
   * It is here because of what the aside IS. Every value on this row is a
   * SECONDARY LABEL beside a field somebody is mid-way through typing into, and
   * the two outcomes are wildly out of proportion: a value this drops costs one
   * missing `·` segment nobody will ever notice, while a value this throws on
   * costs the reader the dialog, everything they had typed into it, and the
   * screen. An advisory warning must not be able to destroy the form it is
   * advising on — and the failure mode here is not hypothetical, it is the one
   * that just happened.
   *
   * ⚠️ It is deliberately NOT applied to `match.name` or `match.reason`. Those
   * two ARE the row: silently swallowing a wrong shape there would leave a
   * blank-but-present duplicate warning, which is the reassuring direction and
   * the one this file refuses everywhere else. If they are ever wrong, they
   * should be wrong loudly.
   */
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text === '' ? null : text;
}

/**
 * The company stage master, loaded for the picker.
 *
 * `/company-stages` is a reference master rather than an enum, so the rows are
 * never hard-coded — Lead / Prospect / Client / Past Client is what exists
 * today, not what the table is limited to. The query schema is `.strict()` and
 * has no `sort`/`order` key, so sending one is a 422; rows already arrive in
 * `sortOrder, name` order and are handed on untouched.
 *
 * `perPage: 200` is the endpoint's ceiling (the default 50 would silently page
 * a master that outgrew it), and `isActive: true` keeps a retired stage out of
 * the offered list — `withStoredOption()` puts the record's own value back if
 * that is where it landed.
 *
 * `failed` is surfaced rather than swallowed: "no stages exist" and "the stages
 * did not arrive" look identical in an empty dropdown, and only one is worth
 * retrying.
 */

/**
 * Create/edit dialog for a company.
 *
 * The inner form is remounted per opening (and per record) so the fields always
 * start from the record rather than from whatever the last session left behind.
 */
export function CompanyFormModal({
  open,
  onClose,
  company,
  onSaved,
  prefill,
  createdContinueLabel,
}: CompanyFormModalProps) {
  if (!open) return null;
  // The prefill is part of the key so a second seed for a still-mounted modal
  // remounts the form rather than keeping the first one's values. A create with
  // no prefill keeps the bare 'new' key it always had.
  const createKey = prefill
    ? `new:${prefill.name ?? ''}:${prefill.countryId ?? ''}`
    : 'new';
  return (
    <CompanyForm
      key={company?.id ?? createKey}
      onClose={onClose}
      company={company}
      onSaved={onSaved}
      prefill={company === undefined ? prefill : undefined}
      createdContinueLabel={createdContinueLabel ?? 'Close'}
    />
  );
}

/*
 * The settled outcome of one similar-companies lookup, paired with the name it
 * describes.
 *
 * ⚠️ A DISCRIMINATED UNION, and the `failed` arm deliberately carries NO `rows`
 * key at all. The shape this replaced was `{name, rows}`, and a failed lookup
 * stored `rows: []` — so "the server answered and nothing is similar" and "we
 * never found out" were the same value, and the component rendered the same
 * nothing for both. A reader then concluded no similar company exists on the
 * strength of a request that never came back.
 *
 * An empty array is not a sentinel for failure here because `tsc` refuses to
 * read `rows` off the failed arm: the two states cannot be merged back together
 * by the next reader without deleting this union first.
 */
type SimilarLookup = { name: string } & (
  | { outcome: 'answered'; rows: SimilarCompany[] }
  | { outcome: 'failed' }
);

/**
 * What the contacts phase of a create actually did.
 *
 * ⚠️ **Kept apart from the company's own outcome on purpose.** They are
 * separate requests with no transaction across them, so "the company was
 * created" and "the contacts were created" are two facts, and the case that
 * matters is the one where the first is true and the second is not. A report
 * collapsing them into one "Saved" is wrong exactly there — the person reads a
 * failure, closes the form, and files the school a second time.
 */
interface ContactWriteOutcome {
  /** How many rows were sent. Blank rows are dropped before this counts them. */
  attempted: number;
  saved: number;
  /**
   * The ones refused, by their **1-based position in the list as submitted**.
   *
   * Position rather than id, because a refused contact HAS no id — that is the
   * whole reason it is in this list — and rather than by name, because the name
   * is often the field that was refused.
   */
  failures: { position: number; name: string; message: string }[];
  /** The server id of each row that landed, by its draft key. */
  idByKey: Record<string, number>;
}

/**
 * What became of the "primary contact" mark, as a THIRD outcome.
 *
 * ⚠️ **`contact-failed` is the branch the owner's design creates and the one
 * that must never be papered over.** If the row somebody marked primary is the
 * row whose `POST /contacts` was refused, there is no contact to designate —
 * and silently promoting whoever else happened to save would put a different
 * person at the top of the customer's Xero record than the one that was asked
 * for, with a success message over it. So it is reported, and nothing is
 * promoted.
 */
type PrimaryOutcome =
  | { kind: 'none' }
  /** `company` is the PATCH's read-back — the freshest copy of the record. */
  | { kind: 'set'; company: Company }
  | { kind: 'contact-failed'; position: number }
  | { kind: 'refused'; message: string };

/**
 * Why one contact was refused, in a sentence a person can act on.
 *
 * ⚠️ **Not `error.message`.** On a 422 that string is only the generic
 * "Validation failed"; the sentence naming the key actually refused lives in
 * `details`. Everything is spelled out in the text because this form renders no
 * control the server's `path` could bind to — a contact's `path` names a
 * `/contacts` key, and every input on this screen belongs to `/companies`.
 *
 * Mirrors `lineFailureMessage` in `QuoteFormModal`, which is the precedent this
 * whole two-phase create follows.
 */
function contactFailureMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return 'The server could not be reached, so this contact was not sent.';
  }
  const parts = [...caught.formMessages];
  for (const [key, message] of Object.entries(caught.fieldErrors)) {
    parts.push(message.includes(key) ? message : `${key}: ${message}`);
  }
  return parts.length > 0 ? parts.join(' ') : caught.message;
}

/**
 * What a company save asked for and the read-back does not show.
 *
 * These schemas are not `.strict()`, so a key the server does not recognise is
 * stripped and the write still succeeds; comparing what came back is the only
 * way to notice. Shared by the create and edit paths.
 */
function unappliedAfterSave(payload: CompanyInput, saved: Company): string[] {
  const unapplied: string[] = [];
  if ('stageId' in payload && (payload.stageId ?? null) !== (saved.stage?.id ?? null)) {
    unapplied.push('its stage');
  }
  if (
    'stageStatusId' in payload &&
    (payload.stageStatusId ?? null) !== (saved.stageStatus?.id ?? null)
  ) {
    unapplied.push('its stage status');
  }
  // `undefined` on the read-back is a server predating the key: not applied.
  if (
    'followUpOn' in payload &&
    payload.followUpOn != null &&
    (saved.followUpOn === undefined || payload.followUpOn !== saved.followUpOn)
  ) {
    unapplied.push('the follow-up date');
  }
  if (
    'xeroSalesLineAmountType' in payload &&
    (payload.xeroSalesLineAmountType ?? null) !== (saved.xeroSalesLineAmountType ?? null)
  ) {
    unapplied.push('the Xero line amount default');
  }
  if ('xeroSalesTaxType' in payload) {
    // Compared case-insensitively: the server normalises a code to Xero's
    // own casing (`output2` is stored `OUTPUT2`), and that is the write
    // being applied, not ignored.
    const asked = payload.xeroSalesTaxType?.toUpperCase() ?? null;
    const got = saved.xeroSalesTaxType?.toUpperCase() ?? null;
    if (asked !== got) unapplied.push('the Xero sales tax rate');
  }
  if (
    'primaryContactId' in payload &&
    (payload.primaryContactId ?? null) !== (saved.primaryContact?.id ?? null)
  ) {
    unapplied.push('the primary contact');
  }
  /*
   * `undefined` on the read-back is a server predating the key, which strips
   * it — so the write did not land, even when the ask was a clear.
   */
  if (
    'billingName' in payload &&
    (saved.billingName === undefined || (payload.billingName ?? null) !== saved.billingName)
  ) {
    unapplied.push('the billing name');
  }
  return unapplied;
}

/**
 * The body for one draft contact.
 *
 * ⚠️ **Built from an explicit key list, never a spread.** Both contact write
 * schemas are `.strict()`, so an unknown key is a 422 naming it — and a spread
 * of the draft row would send `key`, which exists only in this client.
 *
 * ⚠️ **`contactGroupId` is OMITTED when blank, never sent as `''`.** An empty
 * string is a 422 on that path rather than a clear; `null` would be the clear,
 * and there is nothing to clear on a contact being created.
 */
function contactPayload(row: DraftContact, companyId: number): ContactInput {
  // The three name keys, always all three (`lib/contact-name.ts`): a blank
  // last name is sent as `null`, never `''`.
  const lastName = row.lastName.trim();
  const body: ContactInput = {
    companyId,
    firstName: row.firstName.trim(),
    lastName: lastName === '' ? null : lastName,
    name: row.name.trim(),
  };
  const email = row.email.trim();
  if (email !== '') body.email = email;
  const phone = row.phone.trim();
  if (phone !== '') body.phone = phone;
  if (row.contactGroupId !== '') body.contactGroupId = Number(row.contactGroupId);
  return body;
}

/**
 * The primary-contact mark for a contact that ALREADY exists, in the same
 * string key space as a draft row's `key` (`draft-N`), so one radio group and
 * one `primaryKey` state cover both lists on the edit form.
 */
function existingContactKey(contactId: number): string {
  return `contact-${contactId}`;
}

function existingContactId(key: string | null): number | null {
  if (key === null || !key.startsWith('contact-')) return null;
  const id = Number(key.slice('contact-'.length));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** The contacts phase of an EDIT, plus the primary outcome — reported, not latched. */
interface EditContactsOutcome {
  contacts: ContactWriteOutcome | null;
  primary: PrimaryOutcome;
}

/**
 * The company's existing contacts, for the edit form's Contacts section.
 *
 * Same endpoint and page size as `CompanyDetailPage` (`GET
 * /companies/:id/contacts`, 100 by name). `companyId === null` (create) makes
 * no request. `reloadToken` re-reads after the form adds people.
 */
function useCompanyContactRows(companyId: number | null, reloadToken: number) {
  const key = `${companyId ?? ''}:${reloadToken}`;
  /*
   * Keyed by request rather than a separate `loading` flag, so no state is set
   * synchronously inside the effect. The previous rows stay on screen while a
   * re-read after a save is in flight.
   */
  const [result, setResult] = useState<{
    key: string;
    rows: Contact[] | null;
    failed: string | null;
  } | null>(null);

  useEffect(() => {
    if (companyId === null) return;
    const controller = new AbortController();
    api
      .listCompanyContacts(
        companyId,
        { perPage: 100, sort: 'name', order: 'asc' },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({ key, rows: response.data, failed: null });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setResult((prev) => ({
          key,
          rows: prev?.rows ?? null,
          failed: caught instanceof ApiError ? caught.message : 'Couldn’t reach the server.',
        }));
      });
    return () => controller.abort();
  }, [companyId, key]);

  return {
    rows: result?.rows ?? null,
    loading: companyId !== null && result?.key !== key,
    failed: result?.key === key ? result.failed : null,
  };
}

/**
 * The countries master, for the structured country picker.
 *
 * ⚠️ **`rows` is `Country[] | null`, and `null` is NOT an empty list.** "The
 * master is empty" and "we never read it" are the same blank dropdown, and only
 * one of them is a statement about the data — the same discriminated shape as
 * `SimilarLookup` above, and for the same reason.
 *
 * `perPage: 250` is the endpoint's ceiling and 195 rows fit inside it in one
 * page, so there is no `hasMore` to report. **251 is a 422**, so the number is
 * not a knob to turn up. `isActive: true` is a real server-side filter and is
 * opt-in with no default — `ReferenceListParams` says in as many words never to
 * filter these client-side.
 */
function useCountryOptions() {
  const [rows, setRows] = useState<Country[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCountries({ perPage: 250, isActive: true }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRows(response.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows(null);
        setFailed(true);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { rows, loading, failed };
}

/**
 * The contact-groups master, for the group picker on each draft contact row.
 *
 * Fetched only when `enabled`. Both modes enable it since 2026-10-15 (the edit
 * form adds contacts and names each existing contact's group); the switch is
 * kept for a caller that renders no group picker.
 *
 * ⚠️ **Rendered from the response, never from a literal.** `finance` and
 * `operations` are what is seeded today, not what the table is limited to.
 */
function useContactGroups(enabled: boolean) {
  const [rows, setRows] = useState<ContactGroup[] | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listContactGroups({ perPage: 200, isActive: true }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRows(response.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows(null);
        setFailed(true);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { rows, loading, failed };
}

function CompanyForm({
  onClose,
  company,
  onSaved,
  prefill,
  createdContinueLabel,
}: {
  onClose: () => void;
  company?: Company;
  onSaved: (company: Company) => void;
  prefill?: CompanyFormPrefill;
  createdContinueLabel: string;
}) {
  const formId = useId();
  // Resolved from the wrapper so the dialog can open focus on the name input
  // rather than on its close button. `Input` takes no ref of its own.
  const nameRef = useRef<HTMLElement | null>(null);
  const isEdit = company !== undefined;

  /*
   * EDIT ONLY: the record as last read back from a save made by THIS form.
   *
   * An edit that also adds contacts can land part-way (the company PATCH and
   * some contacts saved, another contact refused). The form then stays open on
   * the report, and the caller's `onSaved` is held until it closes — both
   * callers close the modal inside `onSaved`, which would take the report off
   * screen. Diffing against this copy means a retry sends only what is still
   * unsaved rather than repeating a write that already landed.
   */
  const [savedCompany, setSavedCompany] = useState<Company | null>(null);
  const current = savedCompany ?? company;

  const baseline = useMemo(() => toFormValues(current), [current]);
  // The prefill seeds the opening values only — `baseline` stays the empty
  // record, so it is never mistaken for something already stored.
  // EDIT with no stored Billing name: open it at the Company name so the
  // required field does not block the save (the baseline stays blank, so the
  // value is saved as a change).
  const [values, setValues] = useState<FormValues>(() =>
    isEdit
      ? baseline.billingName.trim() === ''
        ? { ...baseline, billingName: baseline.name }
        : baseline
      : withPrefill(baseline, prefill),
  );
  /*
   * CREATE ONLY: the Billing name copies the Company name until the user edits
   * the Billing name itself, then it stays (owner 2026-10-25). An edit starts
   * from the stored Billing name and never follows a rename, because a Company
   * name edit must not reach Xero.
   */
  const [billingFollowsName, setBillingFollowsName] = useState(!isEdit);
  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>({
    contactDetails: false,
    address: false,
    more: false,
    xero: false,
    notes: false,
  });
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /*
   * Set when the server accepted the write but did not apply what was asked.
   *
   * `POST`/`PATCH /companies` are NOT `.strict()` — an unknown body key is
   * silently stripped and the request still succeeds. On PATCH there is an
   * accidental guard: the strip empties the body and the at-least-one-field
   * rule then refuses it. **On POST there is none at all**, because `name` is
   * required, so a valid key always rides along and the typo can never be the
   * only one.
   *
   * The payload here is built from an explicit literal key list, so a typo on
   * THIS side is a compile error rather than a silent write. This guards the
   * other side: a server that accepts and does not apply. Once set, the record
   * is real, so the form stops offering to save it again.
   */
  const [notApplied, setNotApplied] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);

  /*
   * People to add. On create they are typed in before the company exists; on
   * edit (since 2026-10-15) they are added to the existing company, which also
   * lists its current contacts above them. *SUPERSEDED 2026-10-15: "CREATE
   * ONLY — on edit the company's real contacts are managed on its detail
   * page."* The edit form offered no way to add a person, reported as a bug.
   *
   * `primaryKey` spans BOTH lists on edit: a draft row's `key`, or
   * `existingContactKey(id)` for a contact already on file.
   */
  const [draftContacts, setDraftContacts] = useState<DraftContact[]>([]);
  const [contactErrors, setContactErrors] = useState<DraftContactErrors>({});
  const [primaryKey, setPrimaryKey] = useState<string | null>(() =>
    company?.primaryContact ? existingContactKey(company.primaryContact.id) : null,
  );
  const baselinePrimaryKey = current?.primaryContact
    ? existingContactKey(current.primaryContact.id)
    : null;
  const primaryChanged = isEdit && primaryKey !== baselinePrimaryKey;
  const [contactsReload, setContactsReload] = useState(0);
  const existingContacts = useCompanyContactRows(company?.id ?? null, contactsReload);
  const [editOutcome, setEditOutcome] = useState<EditContactsOutcome | null>(null);
  /** Set once an edit has written something; handed to `onSaved` on close. */
  const [pendingSaved, setPendingSaved] = useState<Company | null>(null);

  /*
   * The three outcomes of a create, held separately because they are three
   * requests with no transaction across them.
   *
   * ⚠️ `created` doubles as the "this form has already made a company" latch —
   * once it is set, Save is disabled, because pressing it again would file a
   * SECOND school rather than retry anything.
   */
  const [created, setCreated] = useState<CompanyCreateResult | null>(null);
  const [contactOutcome, setContactOutcome] = useState<ContactWriteOutcome | null>(null);
  const [primaryOutcome, setPrimaryOutcome] = useState<PrimaryOutcome>({ kind: 'none' });
  /*
   * CREATE: the caller's `onSaved` is held until the confirmation is left
   * (owner, 2026-10-16), and fired exactly once. Every caller closes the modal
   * inside `onSaved`, which used to take a partial-contacts report off screen
   * before anybody saw it.
   */
  const createdSavedFired = useRef(false);

  const countries = useCountryOptions();
  const contactGroups = useContactGroups(true);

  /*
   * `withGroups` is on because BOTH pickers below are GATED by a user group:
   * naming somebody outside `account_lead` as `accountLeadId` is a 422 on
   * `/companies`, create and update alike.
   *
   * The unnarrowed `users` list is no longer destructured — it existed for the
   * Owner picker, which is gone. `byGroup` is the whole reason this hook is
   * still called.
   */
  /*
   * ⚠️ `failed` and `hasMore` are no longer destructured either — they fed the
   * two staff sentences removed under the owner's stricter helper-text ruling
   * (2026-09-05) and nothing else read them. The hook still tracks both; this
   * form simply no longer reports them.
   */
  const { loading: usersLoading, byGroup } = useUserOptions(true, true);
  const leadStaff = byGroup[USER_GROUP_SLUGS.accountLead];
  const csmStaff = byGroup[USER_GROUP_SLUGS.clientSuccessManager];
  const stages = useCompanyStages();
  const stageStatuses = useCompanyStageStatuses();

  /*
   * ── Stage-status follow-up inputs (owner 2026-10-23) ──────────────────────
   * Decided by the chosen status's FLAGS (`needsFollowUpDialog`), never its
   * name. Rendered inline in this form rather than as a nested modal.
   *
   * - `followUpOn` ('' = none) is seeded from the record on an edit and sent
   *   only when the status or the date moved (PATCH sends only changed keys).
   * - The reason is asked only when the status itself is being set, which is
   *   the only time the server requires (or reads) it.
   * - "Today" is UTC, as the server's `FOLLOW_UP_DATE_INVALID` check is.
   */
  const [followUpToday] = useState(() => todayUtcIsoDate());
  const followUpMin = nextDayIso(followUpToday);
  const followUpMax = addMonthsIso(followUpToday, 36);
  const [followUpOn, setFollowUpOn] = useState<string>(() => company?.followUpOn ?? '');
  const [stageStatusReason, setStageStatusReason] = useState('');
  const baselineFollowUpOn = current?.followUpOn ?? '';
  const selectedStageStatus =
    values.stageStatusId === ''
      ? null
      : stageStatuses.statuses.find((status) => String(status.id) === values.stageStatusId) ??
        null;
  const stageStatusMoved = values.stageStatusId !== baseline.stageStatusId;
  const asksFollowUp = needsFollowUpDialog(selectedStageStatus);
  const followUpMode = asksFollowUp ? (selectedStageStatus?.resurfaceMode ?? null) : null;
  const asksReason =
    asksFollowUp && stageStatusMoved && selectedStageStatus?.requiresReason === true;
  const warnsMarketing =
    stageStatusMoved && selectedStageStatus?.suppressesMarketing === true;
  /*
   * The date rides on this save: the status moved, or the date did. With the
   * status unchanged, a blank date is not a change for a next-cycle status
   * (the stored date stands; PATCH {} is a 422) and is a validation error for
   * a date status (it cannot be cleared while that status holds).
   */
  const followUpCleared = !stageStatusMoved && followUpOn === '';
  const sendsFollowUpOn =
    followUpMode !== null &&
    (stageStatusMoved ||
      (followUpOn !== baselineFollowUpOn &&
        !(followUpCleared && followUpMode === 'next_cycle')));

  function clearFieldErrors(key: string) {
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  function changeFollowUpOn(next: string) {
    setFollowUpOn(next);
    clearFieldErrors('followUpOn');
  }

  function changeStageStatusReason(next: string) {
    setStageStatusReason(next);
    clearFieldErrors('stageStatusReason');
  }

  /*
   * The status picker's change re-seeds the date. The stored date carries over
   * only when the STORED status resurfaces the same way and the date is still
   * in the future (≥ tomorrow, UTC); otherwise a next-cycle status starts on
   * its computed date and a date status starts blank.
   */
  function changeStageStatus(next: string) {
    setField('stageStatusId', next);
    clearFieldErrors('followUpOn');
    clearFieldErrors('stageStatusReason');
    const status = stageStatuses.statuses.find((row) => String(row.id) === next);
    const mode = status?.resurfaceMode ?? null;
    const storedStatus =
      baseline.stageStatusId === ''
        ? null
        : stageStatuses.statuses.find((row) => String(row.id) === baseline.stageStatusId) ??
          current?.stageStatus ??
          null;
    const storedMode = storedStatus?.resurfaceMode ?? null;
    const storedOn = current?.followUpOn ?? null;
    if (mode !== null && storedMode === mode && storedOn != null && storedOn >= followUpMin) {
      setFollowUpOn(storedOn);
    } else if (mode === 'next_cycle') {
      setFollowUpOn(nextCycleFollowUpDate(followUpToday));
    } else {
      setFollowUpOn('');
    }
  }
  /*
   * `true` is passed rather than relying on the default: this form exists to
   * write, so there is no read-only branch here that could skip the request the
   * way the detail page's `canUpdate` does.
   */
  const sources = useCompanySources(true);
  /* `true` for the same reason as the master above it: this form only ever writes. */
  const companyTypes = useCompanyTypes(true);

  /*
   * ── A NEW company starts with the signed-in person as its ACCOUNT LEAD ────
   *
   * ⚠️ **Only when they are really in the `account_lead` group.** Owner's
   * decision: **seed a member, leave everybody else on the blank row.** This is
   * not an always-seed waiting to be finished. ⚠️ *Read as "leave everybody
   * else Unassigned" until 2026-09-18 — and that word was the bug: the blank
   * row on a create does NOT mean unassigned. See below.*
   *
   * 🔴 **CORRECTED 2026-09-18 — THE REASON WAS WRONG ABOUT THE SERVER, and the
   * control it justifies is right, so no artefact anywhere could ever have
   * contradicted it.** *SUPERSEDED: "`accountLeadId` is enforced server-side
   * against that group — naming a non-member is a live 422 on create — so
   * seeding whoever happens to be signed in would pre-fill a value the server
   * then refuses, on a field the person never touched, in a picker that does not
   * offer them."*
   *
   * **The create path is the ONE EXCEPTION to that guard, and it is decided
   * rather than accidental.** Measured in the backend's `companies.service.ts`
   * on 2026-09-18: `assertStaffExist(input)` — which ends in
   * `assertLeadsInGroups(input)` — runs against the PARSED BODY at `:1829`,
   * while `accountLeadDefault` is spread into `columns` fifty lines later
   * (`:1879-1885`) and never onto `input`, so the default can never be handed
   * back to a check that would refuse it. The service states the position *is*
   * the whole of the exception's scope, at `:1860-1867`. **A non-member is not
   * refused. They are stored.**
   *
   * ✅ **The narrowing stands anyway, on the half of that sentence that IS true:
   * a lead this form SENDS is checked.** `buildPayload` emits `accountLeadId`
   * whenever the picker holds a value, so seeding a non-member would convert a
   * silent server-side default into a live 422 on the create — and the same id
   * re-sent from any later edit is a 422 too, because `updateCompany` runs the
   * guard on what it is given. ⛔ **Do not widen this seed.**
   *
   * ✅ **WHAT THE OLD SENTENCE HID IS FIXED ON SCREEN — 2026-09-18.**
   * "Unassigned" WAS a false statement here. The default fires on
   * `accountLeadId === undefined` and a blank picker is exactly that — the
   * create branch of `buildPayload` OMITS the key, where the edit branch sends
   * an explicit `null` — so a NON-MEMBER creating a company read **Unassigned**
   * and was stored as the account lead regardless, as did anybody creating one
   * before the evidence arrived (`narrowed: false`, the directory still out, or
   * the read failed). **The form stated the opposite of what the server does,
   * with no error anywhere to find it by.**
   *
   * **On a create the blank row now reads `creatorLeadLabel(...)`** — *"You —
   * Priya Nair"* — which is true in every one of those states: seeded, cleared,
   * member, non-member. ⚠️ **It needs no membership test of its own, and that
   * is why it is safe**: the server assigns the caller whoever they are, so
   * one label covers the lot. The EDIT side still reads "Unassigned" and
   * always could — there is no creator default on a PATCH.
   *
   * ⚠️ **THE DEFAULT BRANCHES ON THE CREDENTIAL, AND THIS APP IS ALWAYS THE
   * BRANCH THAT ASSIGNS THE CALLER.** `accountLeadDefault` answers
   * `creatorAsAccountLead` for a `session` and something else for an API key —
   * that second arm was read off an UNCOMMITTED backend tree on 2026-09-18 and
   * is **not** recorded here as live. It changes nothing for this form either
   * way: every request from `src/` carries a bearer token and never an API key,
   * so the creator arm is the only one this screen can reach.
   *
   * ✅ **AND A COMPANY CAN NOW BE CREATED HERE WITH NO LEAD AT ALL — owner's
   * decision 2026-09-18**, chosen from three options with their costs stated,
   * and this is the one create path in `src/`. `NO_ACCOUNT_LEAD_OPTION` sends
   * an explicit `null`, which the group guard is exempt from and which leaves
   * `d_companies.account_lead_id` NULL. It matters because "no account lead" is
   * a deliberate state rather than a gap.
   *
   * ⛔ **THE BLANK ROW AND THAT ROW MUST STAY TWO THINGS.** An absent key and an
   * explicit `null` are opposite instructions on this field, one keystroke
   * apart, and collapsing them is how the creator default gets retired by
   * accident — the option the owner did NOT choose.
   *
   * ⚠️ **The evidence is `leadStaff`, and it is the SAME list the picker
   * offers** — the group-narrowed view of the directory this form already
   * fetched. No second request, and no second answer to "who may lead an
   * account" that could disagree with the dropdown standing next to it.
   *
   * ⚠️ **`narrowed` is the load-bearing half.** When the group master is
   * unreadable or answers 404, `narrowStaffToGroup` hands back the WHOLE
   * directory with `narrowed: false` — so a membership test against it would
   * pass for everybody on the strength of a lookup that never resolved. A list
   * that has not been narrowed is not evidence of membership.
   *
   * The other two unknown states need no test of their own and deliberately get
   * none: while `/users` is still out, and after it has failed, `leadStaff.users`
   * is empty, so the match fails and nothing is seeded. **An unread list is not
   * evidence that somebody is not in the group** — the same reading
   * `withStoredOption` and the project form's retired-city refusal take — so the
   * seed simply does not happen, rather than happening on a guess.
   *
   * `user` is the identity this app is ACTING AS, which during a support session
   * is the target rather than the super admin. That is the right person: it is
   * whose picker, whose group and whose company this is.
   */
  const { user: signedInUser, ability } = useAuth();
  /*
   * Edit only: adding a person is `contacts.create`, a different grant from the
   * `companies.update` that opened this form. The create form keeps its rows
   * ungated, as before. The server's 403 remains the real boundary.
   */
  const canAddContacts = !isEdit || ability.canCreate('contacts');
  const seededLeadId = useMemo(() => {
    if (isEdit || !signedInUser) return null;
    if (!leadStaff.narrowed) return null;
    return leadStaff.users.some((staff) => staff.id === signedInUser.id)
      ? String(signedInUser.id)
      : null;
  }, [isEdit, signedInUser, leadStaff]);

  /*
   * ⚠️ **"Untouched" is tracked EXPLICITLY, and CLEARING COUNTS AS TOUCHING.**
   * The ref is written by the Account lead picker's own `onChange` and by
   * nothing else, so removing the seeded name leaves the field empty for good.
   * ⚠️ **On a CREATE that empties the CONTROL and not the COLUMN** — a blank
   * picker omits the key, and an omitted key is precisely what the server
   * defaults to the creator. **Clearing the seed does not produce a company
   * with no lead**; since 2026-09-18 it lands on a blank row that says so
   * (*"You — Priya Nair"*), and the way to mean nobody is the separate
   * `NO_ACCOUNT_LEAD_OPTION` row. ⚠️ **Picking that row also counts as
   * touching** — it goes through the same `onChange` — so the seed cannot
   * reinstate itself over it.
   * Inferring it from `values.accountLeadId === ''` instead would re-seed on the
   * very next render — or the moment the staff list resolved — and the control
   * would fight the person using it, which is the failure this codebase already
   * records against the seeded document name.
   *
   * It starts TRUE on an edit, which is not a long way of writing `isEdit`: an
   * existing company's lead is its own, and so is its stored ABSENCE. Neither is
   * a gap for this form to fill in. `seededLeadId` is null on an edit as well —
   * two guards, because re-deriving a lead onto a live record would silently
   * reassign an account with a 200 and nothing to restore from.
   */
  const leadTouched = useRef(isEdit);
  useEffect(() => {
    if (isEdit || leadTouched.current || seededLeadId === null) return;
    setValues((prev) =>
      prev.accountLeadId === '' ? { ...prev, accountLeadId: seededLeadId } : prev,
    );
  }, [isEdit, seededLeadId]);

  /*
   * The tax picker's options come from the connected tenant, live — never from
   * a table of ours, because the codes are tenant-specific and mutable and a
   * stale copy is not a validation error, it is a wrong VAT figure on a
   * customer's invoice. Nothing is requested while the connection is still
   * being read or is absent: there is no tenant to ask.
   */
  const xero = useXero();
  const taxRates = useXeroTaxRates(!xero.loading && xero.connection.connected);

  /*
   * Companies that already exist under a name like the one being typed.
   *
   * ⚠️ NOT HELPER TEXT, and it must survive a prose sweep. The test the
   * exception list applies — *what wrong conclusion does this prevent?* — has a
   * concrete answer here: it names a state the reader cannot otherwise see.
   * `d_companies.name` carries no unique constraint (deliberately: subsidiaries
   * and re-registrations legitimately share a name), so nothing downstream will
   * ever tell somebody that the school they are adding is already on file. The
   * only place that fact can appear is here, while they are still typing it.
   *
   * ⚠️ It is ADVISORY and must never gate the save. `HSNC University - K C
   * College` and `HSNC University - Thadomal College of Engineering` are two
   * customers that bill separately, and a near-match is a word-order or
   * spelling variant as often as it is the same organisation. Hence "looks
   * similar to" and no merge affordance anywhere below — a block here would
   * make a legitimate school unsaveable with nothing on screen explaining why.
   */
  /*
   * Stored WITH the name they describe, never as a bare array.
   *
   * The name changes on every keystroke and the rows arrive 250ms and one
   * round trip later, so "the rows in state" and "the name on screen" are
   * routinely about different things. Pairing them makes the render condition
   * `rows.name === lookupName` — matches are shown against the exact name they
   * were fetched for, or not at all — instead of a sequence of `setSimilar`
   * calls that has to clear the old rows before the new name is typed and is
   * silently wrong for 250ms whenever it does not.
   *
   * The same pairing is what makes the failure state safe to render: a lookup
   * that failed for a name nobody is typing any more must not report a failure
   * about the name that is on screen now.
   *
   * `null` is "nothing has settled for any name yet" — never asked, or asked
   * and still out. Both render nothing, which is the documented behaviour: no
   * spinner, no flicker per keystroke.
   */
  const [similar, setSimilar] = useState<SimilarLookup | null>(null);

  /*
   * Create only. On edit the company already exists, so the strongest match is
   * always the record itself and the rest is noise about a name nobody is
   * choosing. Gated on `isEdit` — the same `company !== undefined` signal the
   * rest of the form splits create from edit on — rather than on a second flag
   * that could disagree with it.
   */
  const lookupName = isEdit ? '' : values.name.trim();

  /*
   * Debounced at 250ms, matching every other typed lookup in the app: the
   * office shares one per-IP bucket, and a request per keystroke on a field
   * somebody types a whole school name into is the fastest way into it.
   *
   * ⚠️ *SUPERSEDED: "the office shares one 120/60s-per-IP bucket."* It is
   * **12,000/60s** (corrected 2026-09-17). Debouncing is still right — a
   * request per keystroke is the wrong shape at any ceiling, and it is the
   * house rule for every typed lookup here — but the arithmetic that reason
   * cited is off by a factor of a hundred.
   *
   * ⚠️ A failed lookup is RECORDED AS A FAILURE, not stored as zero rows. Both
   * of the restraints this block used to justify silence with still hold and
   * are kept: a TONED block because a background request timed out would read
   * as a verdict on a name that is perfectly fine, and a spinner appearing and
   * vanishing on every keystroke is the noise this whole area exists to cut
   * through. What silence could not do was distinguish itself from "nothing
   * similar exists" — see `SimilarLookup`. So the third state is rendered, once
   * settled, as one muted line and nothing more.
   *
   * An ABORTED lookup still records nothing: an abort means the name moved on,
   * and the render condition already hides an answer about an older name.
   *
   * There is still no `.finally` because there is still no in-flight state to
   * unwind — nothing is rendered while a request is out, so nothing has to be
   * cleared when it lands.
   */
  useEffect(() => {
    // Nothing to ask about, and nothing to clear: whatever is in state names a
    // different string, so the render condition already hides it.
    if (lookupName === '') return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .listSimilarCompanies({ name: lookupName, limit: SIMILAR_LIMIT }, controller.signal)
        .then((response) => {
          if (controller.signal.aborted) return;
          setSimilar({ name: lookupName, outcome: 'answered', rows: response.data });
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setSimilar({ name: lookupName, outcome: 'failed' });
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [lookupName]);

  /*
   * Only ever the outcome fetched for the name currently in the box. Both
   * derived values below read this one guard, so a failure and a match set can
   * never be describing different names as each other.
   */
  const settledSimilar = similar !== null && similar.name === lookupName ? similar : null;
  const similarMatches = settledSimilar?.outcome === 'answered' ? settledSimilar.rows : [];
  const similarLookupFailed = settledSimilar?.outcome === 'failed';

  // Server messages win: they are the authority on what the API rejected.
  const errors = useMemo(
    () => ({ ...clientErrors, ...serverErrors }),
    [clientErrors, serverErrors],
  );

  /*
   * The diff. Comparing trimmed values means a stray space never counts as a
   * change, so an edit that touched nothing real still sends nothing at all.
   */
  const changedTextKeys = useMemo(
    () => TEXT_KEYS.filter((key) => values[key].trim() !== baseline[key].trim()),
    [values, baseline],
  );
  const changedStaffKeys = useMemo(
    () => STAFF_KEYS.filter((key) => values[key] !== baseline[key]),
    [values, baseline],
  );
  /*
   * The picklist half of the deactivation guard: `stageId` is sent ONLY when the
   * user moved the picker. `stageId` is checked for existence and not for
   * `isActive`, so re-sending a retired id would be accepted here rather than
   * rejected — but the rule is kept anyway, because an untouched key belongs in
   * no PATCH regardless of whether the server would tolerate it.
   */
  const changedPicklistKeys = useMemo(
    () => PICKLIST_KEYS.filter((key) => values[key] !== baseline[key]),
    [values, baseline],
  );
  const changedXeroKeys = useMemo(
    () => XERO_KEYS.filter((key) => values[key] !== baseline[key]),
    [values, baseline],
  );
  const isDirty =
    changedTextKeys.length > 0 ||
    changedStaffKeys.length > 0 ||
    changedPicklistKeys.length > 0 ||
    changedXeroKeys.length > 0 ||
    sendsFollowUpOn;

  /*
   * Which Xero defaults the user has moved back to "use organisation settings".
   *
   * This is the one transition the UI must narrate. Sending `null` clears OUR
   * value and stops us sending a default — it does NOT clear the override
   * already stored on the Xero contact, because Xero has no way to express
   * "unset" and omitting a key means leave it alone. Left unsaid, EMERGE would
   * read one way and Xero keep the old override with nothing flagging it.
   */
  const clearedXeroDefaults = useMemo(() => {
    if (!isEdit) return [] as string[];
    const cleared: string[] = [];
    if (baseline.xeroSalesLineAmountType !== '' && values.xeroSalesLineAmountType === '') {
      cleared.push('line amounts');
    }
    if (baseline.xeroSalesTaxType !== '' && values.xeroSalesTaxType === '') {
      cleared.push('the sales tax rate');
    }
    return cleared;
  }, [isEdit, baseline, values]);

  function setField(key: TextKey | StaffKey | PicklistKey | XeroKey, next: string) {
    /*
     * A stage status belongs to one stage, so moving Stage clears it (owner
     * 2026-10-23). The server does the same on a stage change; clearing here
     * keeps the form from offering a value the new stage cannot hold.
     */
    const clearsStatus = key === 'stageId';
    const copiesToBilling = key === 'name' && billingFollowsName;
    if (key === 'billingName') setBillingFollowsName(false);
    setValues((prev) =>
      clearsStatus && prev.stageId !== next
        ? { ...prev, stageId: next, stageStatusId: '' }
        : copiesToBilling
          ? { ...prev, name: next, billingName: next }
          : { ...prev, [key]: next },
    );
    // An error about a value the user has since changed is noise, not guidance.
    setClientErrors((prev) => {
      let rest = key in prev ? without(prev, key) : prev;
      if (copiesToBilling && 'billingName' in rest) rest = without(rest, 'billingName');
      return rest;
    });
    setServerErrors((prev) => {
      let rest = key in prev ? without(prev, key) : prev;
      if (clearsStatus && 'stageStatusId' in rest) rest = without(rest, 'stageStatusId');
      if (copiesToBilling && 'billingName' in rest) rest = without(rest, 'billingName');
      return rest;
    });
  }

  /*
   * PATCH semantics: an omitted key is left untouched, an explicit null clears
   * the field. So the edit payload carries *only* what changed — spreading the
   * whole record would blank every column the user never opened. Create takes
   * the opposite tack: send what was filled in, omit the rest.
   */
  function buildPayload(): CompanyInput {
    const payload: CompanyInput = {};

    if (company) {
      for (const key of changedTextKeys) {
        const next = values[key].trim();
        if (key === 'name') {
          payload.name = next;
        } else if (key === 'billingName') {
          // Required: validation blocks a blank, so this never sends null.
          if (next !== '') payload.billingName = next;
        } else if (key === 'layout') {
          // Never nullable server-side; validation blocks clearing it.
          if (next !== '') payload.layout = next;
        } else {
          payload[key] = next === '' ? null : next;
        }
      }
      /*
       * ⚠️ **UNCHANGED, AND THAT IS THE POINT: on an EDIT a blank picker
       * already means NOBODY and already sends `null`, so "Unassigned" on it
       * is TRUE here.** The explicit "No account lead" row is create-only for
       * exactly that reason — offering it here would be two rows doing one
       * thing, which is the cost this design was weighed against.
       *
       * The sentinel is folded anyway and **cannot arrive today** (the row is
       * withheld when `isEdit`). It is written out rather than left to a
       * reachability argument because the price of that argument going stale
       * is `Number('__none')` → `NaN` sent as an account lead, and the price
       * of the test is one comparison.
       */
      for (const key of changedStaffKeys) {
        const value = values[key];
        payload[key] = value === '' || value === NO_ACCOUNT_LEAD ? null : Number(value);
      }
      // An explicit null here is meaningful, not a blank: it puts the company
      // back to unclassified, which is a state the column is nullable for.
      for (const key of changedPicklistKeys) {
        payload[key] = values[key] === '' ? null : Number(values[key]);
      }
      /*
       * Written out key by key rather than looped, so each one carries its own
       * type. `null` is meaningful here and is sent deliberately: it is how
       * EMERGE stops sending a default at all. See `clearedXeroDefaults` for
       * what that does and does not do to the Xero contact.
       */
      if (changedXeroKeys.includes('xeroSalesLineAmountType')) {
        payload.xeroSalesLineAmountType = toLineAmountType(values.xeroSalesLineAmountType);
      }
      if (changedXeroKeys.includes('xeroSalesTaxType')) {
        // The CODE, never the name. `xeroSalesTaxName` is read-only and is
        // never in this payload — it is not even in `FormValues`.
        payload.xeroSalesTaxType =
          values.xeroSalesTaxType === '' ? null : values.xeroSalesTaxType;
      }
      /*
       * EDIT ONLY, and outside `FormValues` on purpose (see the note on
       * `StaffKey`: the key must stay unreachable from the create branch).
       * Moving the mark to an existing contact, or clearing it, rides on this
       * PATCH; marking a DRAFT row waits until that contact exists, and is the
       * separate request in `setPrimaryContact`.
       */
      if (primaryChanged) {
        const existingId = existingContactId(primaryKey);
        if (primaryKey === null) payload.primaryContactId = null;
        else if (existingId !== null) payload.primaryContactId = existingId;
      }
      addFollowUpKeys(payload);
      return payload;
    }

    payload.name = values.name.trim();
    for (const key of TEXT_KEYS) {
      if (key === 'name') continue;
      const next = values[key].trim();
      if (next !== '') payload[key] = next;
    }
    /*
     * 🔴 **THREE OUTCOMES, AND THE ONE THAT LOOKS LIKE SILENCE IS AN
     * INSTRUCTION.** On `accountLeadId` an omitted key is not an absence:
     * `accountLeadDefault` fires on `accountLeadId === undefined` alone, so a
     * blank picker is what makes the signed-in person the lead.
     *
     *   a named id     -> sent, and GROUP-CHECKED by the server
     *   NO_ACCOUNT_LEAD -> explicit `null`, exempt from that check, nobody leads
     *   blank           -> key OMITTED, and the server assigns the creator
     *
     * ⛔ **THE LAST TWO MUST NEVER COLLAPSE INTO ONE.** They are one keystroke
     * apart in this picker and they are opposite instructions — the standing
     * `[] destroys, absent leaves alone` rule, arriving on a scalar. The blank
     * row is labelled `creatorLeadLabel(...)` on create precisely so the reader
     * can tell them apart before they choose, rather than after a `201`.
     *
     * ⛔ **Do not "harmonise" this loop with the edit one.** Sending `null` from
     * the blank arm would leave every new company with no lead and quietly
     * retire the owner's ruling that a requester leads the company they raised.
     * That was put to the owner on 2026-09-18 as one of three options and is
     * NOT what they chose; they chose the explicit row.
     *
     * ⚠️ `clientSuccessManagerId` shares this loop and has **no server-side
     * default of any kind**, so for it a blank really is an absence and the
     * sentinel arm is unreachable — its picker is never given the row.
     */
    for (const key of STAFF_KEYS) {
      const value = values[key];
      if (value === NO_ACCOUNT_LEAD) {
        payload[key] = null;
      } else if (value !== '') {
        payload[key] = Number(value);
      }
    }
    /*
     * Left alone, the key is omitted rather than sent as null. A new company is
     * unclassified until somebody classifies it, and defaulting it to the first
     * stage would assert something about a real organisation that nobody said.
     */
    for (const key of PICKLIST_KEYS) {
      if (values[key] !== '') payload[key] = Number(values[key]);
    }
    /*
     * Omitted rather than sent as null when untouched. A new company has no
     * Xero default until somebody chooses one, and null and absent mean the
     * same thing to the server here — but only one of them makes a claim.
     */
    if (values.xeroSalesLineAmountType !== '') {
      payload.xeroSalesLineAmountType = toLineAmountType(values.xeroSalesLineAmountType);
    }
    if (values.xeroSalesTaxType !== '') payload.xeroSalesTaxType = values.xeroSalesTaxType;
    addFollowUpKeys(payload);
    return payload;
  }

  /*
   * The follow-up keys, shared by both branches. The date is sent only while
   * the chosen status resurfaces AND the status or the date moved; a blank date
   * is omitted (a next-cycle status then takes the server's default). The
   * reason only rides with the status change that requires it.
   */
  function addFollowUpKeys(payload: CompanyInput) {
    if (sendsFollowUpOn && followUpOn !== '') payload.followUpOn = followUpOn;
    if (asksReason) payload.stageStatusReason = stageStatusReason.trim();
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    if (values.name.trim() === '') found.name = `${COMPANY_NAME_LABEL} is required.`;
    if (values.billingName.trim() === '') found.billingName = `${BILLING_NAME_LABEL} is required.`;

    for (const key of TEXT_KEYS) {
      if (values[key].trim().length > MAX_LENGTHS[key]) {
        found[key] = `Must be ${MAX_LENGTHS[key]} characters or fewer.`;
      }
    }

    for (const key of ['email', 'secondaryEmail'] as const) {
      const value = values[key].trim();
      if (value !== '' && !EMAIL_PATTERN.test(value)) {
        found[key] = 'Enter a valid email address.';
      }
    }

    if (isEdit && baseline.layout.trim() !== '' && values.layout.trim() === '') {
      found.layout = 'Layout always has a value and cannot be cleared.';
    }

    if (sendsFollowUpOn) {
      if (followUpOn === '') {
        if (followUpMode === 'date') found.followUpOn = 'Choose a follow-up date.';
      } else if (
        !ISO_DATE_PATTERN.test(followUpOn) ||
        followUpOn < followUpMin ||
        followUpOn > followUpMax
      ) {
        found.followUpOn = 'Choose a date after today and within 3 years.';
      }
    }
    if (asksReason && stageStatusReason.trim() === '') {
      found.stageStatusReason = 'Enter a reason.';
    }

    return found;
  }

  /**
   * The draft rows that will actually be sent, in submission order.
   *
   * Blank rows are dropped rather than refused — somebody who pressed "Add
   * contact" and changed their mind must not be blocked from saving the company
   * by a box they never filled. The positions reported back are indexes into
   * THIS list, which is why it is derived once and used by the validator, the
   * writer and the report alike.
   */
  const contactsToWrite = useMemo(
    () => draftContacts.filter((row) => !isBlankDraftContact(row)),
    [draftContacts],
  );

  function validateContacts(): DraftContactErrors {
    const found: DraftContactErrors = {};
    for (const row of contactsToWrite) {
      const rowErrors: { firstName?: string; name?: string; email?: string } = {};
      // First name is required on every contact form (owner, 2026-10-16); the
      // full name is the one key `POST /contacts` itself requires.
      if (row.firstName.trim() === '') {
        rowErrors.firstName = `${CONTACT_NAME_LABELS.firstName} is required.`;
      }
      if (row.name.trim() === '') rowErrors.name = `${CONTACT_NAME_LABELS.name} is required.`;
      const email = row.email.trim();
      if (email !== '' && !EMAIL_PATTERN.test(email)) {
        rowErrors.email = 'Enter a valid email address.';
      }
      if (Object.keys(rowErrors).length > 0) found[row.key] = rowErrors;
    }
    return found;
  }

  /**
   * Phase two: write each draft contact against the company that now exists.
   *
   * ⚠️ **One request per contact, sequential, and it does NOT stop at the first
   * failure.** There is no bulk endpoint and no transaction, so a partial
   * result is an ordinary outcome rather than an edge case. Stopping early
   * would lose the rows after the bad one and leave the person working out
   * which had landed.
   *
   * Sequential rather than `Promise.all` because three contacts do not need
   * racing.
   *
   * ⚠️ *SUPERSEDED: "because the office shares one 120-per-60-seconds bucket
   * and three contacts do not need racing."* The limit is **12,000/60s**
   * (corrected 2026-09-17), so it was never the binding reason. Sequential is
   * still right: the loop reports per row and does not stop at the first
   * failure, which a race makes harder to attribute.
   *
   * ⚠️ **Nothing is rolled back and nothing pretends to be.** The company
   * exists from the moment the create returned; deleting it to "undo" a refused
   * contact would destroy the part that worked.
   */
  async function writeDraftContacts(
    companyId: number,
    rows: DraftContact[],
  ): Promise<ContactWriteOutcome> {
    const failures: ContactWriteOutcome['failures'] = [];
    const idByKey: Record<string, number> = {};
    let saved = 0;

    for (const [index, row] of rows.entries()) {
      try {
        const result = await api.createContact(contactPayload(row, companyId));
        idByKey[row.key] = result.data.id;
        saved += 1;
      } catch (caught) {
        failures.push({
          position: index + 1,
          name: row.name.trim(),
          message: contactFailureMessage(caught),
        });
      }
    }

    return { attempted: rows.length, saved, failures, idByKey };
  }

  /**
   * Phase three: designate the marked contact as primary.
   *
   * ⚠️ **A third request, because `POST /companies` REFUSES a non-null
   * `primaryContactId`** — a contact cannot belong to a company that does not
   * exist yet. So it can only ever be a PATCH after both earlier phases.
   *
   * ⚠️ **If the marked row is the one that failed, that is REPORTED and nobody
   * else is promoted.** Silently designating whoever did save would put a
   * different person at the head of the customer's Xero record than the one
   * that was asked for, under a success message — the specific failure this
   * design creates, so it gets its own branch and its own sentence.
   */
  async function setPrimaryContact(
    companyId: number,
    rows: DraftContact[],
    outcome: ContactWriteOutcome,
  ): Promise<PrimaryOutcome> {
    if (primaryKey === null) return { kind: 'none' };

    const position = rows.findIndex((row) => row.key === primaryKey) + 1;
    if (position === 0) return { kind: 'none' };

    const contactId = outcome.idByKey[primaryKey];
    if (contactId === undefined) return { kind: 'contact-failed', position };

    try {
      const saved = await api.updateCompany(companyId, { primaryContactId: contactId });
      return { kind: 'set', company: saved.data };
    } catch (caught) {
      /*
       * The server's own sentence, verbatim. There are FIVE distinct refusals
       * on this path — the contact is half-named, has opted out of marketing
       * email, and three membership cases — and every one of them is a fact
       * about that person that this form cannot re-derive. Nothing of ours is
       * put in front of it.
       */
      return { kind: 'refused', message: contactFailureMessage(caught) };
    }
  }

  function revealSectionsWithErrors(found: Record<string, string>) {
    setOpenSections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of SECTION_IDS) {
        if (!next[id] && SECTION_FIELDS[id].some((key) => key in found)) {
          next[id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    const contactsFound = validateContacts();
    setClientErrors(found);
    setContactErrors(contactsFound);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0 || Object.keys(contactsFound).length > 0) {
      /*
       * `contactsFound` needs no reveal of its own: the Contacts block is
       * rendered permanently open, so a per-row error is already on screen.
       * It used to be opened explicitly here, because those errors are
       * client-side and no server `path` will ever name one.
       */
      revealSectionsWithErrors(found);
      return;
    }

    const payload = buildPayload();

    // An edit has its own three-phase path: PATCH (if anything changed), then
    // the added contacts, then a primary mark on a newly added row.
    if (current) {
      await submitEdit(current, payload);
      return;
    }

    // PATCH {} is a 422. Save is already disabled when nothing changed; this is
    // the belt to that pair of braces.
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result = company
        ? await api.updateCompany(company.id, payload)
        : await api.createCompany(payload);

      /*
       * The response is the truth, not the status code. Reported either way so
       * the list reflects what was actually stored — reporting nothing would
       * leave the caller showing a stage the server never accepted.
       *
       * 🔴 2026-10-16: a CREATE no longer reports here. The window turns into
       * the confirmation and `onSaved` fires when it is left (`handleClose`).
       * *SUPERSEDED: `onSaved(result.data)` here for both paths.*
       */
      if (company) onSaved(result.data);

      /*
       * The read-back, extended to the Xero defaults for the same reason it
       * exists for the stage: these schemas are not `.strict()`, so a key the
       * server does not recognise is stripped and the write still succeeds.
       * Comparing what came back is the only way to notice.
       */
      const unapplied = unappliedAfterSave(payload, result.data);

      /*
       * ⚠️ **`countryId` is deliberately NOT read back here.** The comparison
       * would be against `result.data.country?.id`, and the embed's field set is
       * the one part of this contract taken from a description rather than
       * measured — so a server that names the key differently, or has not put it
       * on the read mapper yet, would make every successful country write look
       * unapplied. A read-back is only evidence when the read shape is known;
       * against an unmeasured one it manufactures a fault. Add it once the embed
       * has been seen on the wire.
       */

      /*
       * Phases two and three, both CREATE ONLY. Each is its own request with no
       * transaction across it or across the create above, which is exactly why
       * the three outcomes are held separately and reported separately below.
       */
      let contacts: ContactWriteOutcome | null = null;
      let primary: PrimaryOutcome = { kind: 'none' };
      if (!company) {
        // Latched before either await: the company is real from here on, so a
        // second press of Save must never file a second school.
        setCreated(result.data as CompanyCreateResult);
        if (contactsToWrite.length > 0) {
          contacts = await writeDraftContacts(result.data.id, contactsToWrite);
          setContactOutcome(contacts);
          primary = await setPrimaryContact(result.data.id, contactsToWrite, contacts);
          setPrimaryOutcome(primary);
        }
      }

      if (unapplied.length > 0) {
        const list = joinList(unapplied);
        const verb = unapplied.length === 1 ? 'was' : 'were';
        const pronoun = unapplied.length === 1 ? 'it' : 'them';
        setNotApplied(
          company
            ? `The company was saved, but ${list} ${verb} not applied. Set ${pronoun} again here.`
            : `${list.charAt(0).toUpperCase()}${list.slice(1)} ${verb} not applied.`,
        );
      }

      /*
       * ⚠️ **Held open whenever ANY phase fell short, and closed only when every
       * one of them landed.** Closing on a partial result would take the report
       * off screen at the exact moment it is the only record that a contact was
       * refused — and the person would close the form believing the whole thing
       * failed and create the school again.
       */
      /*
       * 🔴 2026-10-16 (owner): a CREATE always stays open on the confirmation,
       * full success included. *SUPERSEDED: closed when every phase landed.*
       */
      if (!company) {
        setSubmitting(false);
        return;
      }

      const partial =
        unapplied.length > 0 ||
        (contacts !== null && contacts.failures.length > 0) ||
        primary.kind === 'contact-failed' ||
        primary.kind === 'refused';

      if (partial) {
        setSubmitting(false);
        return;
      }

      onClose();
    } catch (caught) {
      reportSaveError(caught);
      setSubmitting(false);
    }
  }

  function reportSaveError(caught: unknown) {
    /*
     * Matched on the CODE, with or without a `details` path: another company
     * already has this Billing name (409), or the Billing name was blank (422).
     * Either way the Billing name control is where it is fixed. The Company
     * name is not unique (owner 2026-10-25), so nothing routes to `name` here.
     */
    if (
      caught instanceof ApiError &&
      (caught.code === 'COMPANY_BILLING_NAME_TAKEN' || caught.code === 'BILLING_NAME_REQUIRED')
    ) {
      const fields = { billingName: caught.fieldErrors.billingName ?? caught.message };
      setServerErrors(fields);
      revealSectionsWithErrors(fields);
      return;
    }
    /*
     * The status no longer belongs to the stage being saved (or there is no
     * stage). Bound to the Stage status control by CODE, so it lands there even
     * if the refusal arrives without a `details` path.
     */
    if (caught instanceof ApiError && caught.code === 'STAGE_STATUS_WRONG_STAGE') {
      setServerErrors({
        stageStatusId: caught.fieldErrors.stageStatusId ?? caught.message,
      });
      return;
    }
    /*
     * The follow-up refusals, bound by CODE to the inline controls under the
     * status picker, with or without a `details` path.
     */
    if (
      caught instanceof ApiError &&
      typeof caught.code === 'string' &&
      FOLLOW_UP_DATE_CODES.has(caught.code)
    ) {
      setServerErrors({ followUpOn: caught.fieldErrors.followUpOn ?? caught.message });
      return;
    }
    if (caught instanceof ApiError && caught.code === 'STAGE_STATUS_REASON_REQUIRED') {
      setServerErrors({
        stageStatusReason: caught.fieldErrors.stageStatusReason ?? caught.message,
      });
      return;
    }
    if (caught instanceof ApiError) {
      /*
       * Split against this form's OWN field list, never against the shape of
       * the path. Only `fields` may be marked, revealed and counted — a
       * section is opened to show an error, so routing one to a key with no
       * control here would open a disclosure over nothing.
       *
       * `unroutable` is everything no input can carry: the EMPTY-path
       * `.strict()` and "at least one field" refusals, plus any path naming a
       * key this form does not render (including `primaryContactId`, whose
       * five refusals are the server's own sentences). Rendering it at form
       * level is the only way it is seen at all, and it is never counted as
       * highlighted.
       */
      const { fields, unroutable } = caught.routeFieldErrors(ROUTABLE_WRITE_KEYS);
      setServerErrors(fields);
      revealSectionsWithErrors(fields);
      if (unroutable.length > 0) setFormError(unroutable.join(' '));
      else if (Object.keys(fields).length === 0) setFormError(caught.message);
    } else {
      setFormError('Couldn’t reach the server. Check your connection.');
    }
  }

  /**
   * The EDIT save: up to three requests with no transaction across them.
   *
   * 1. `PATCH /companies/:id` — only if a company field (or the primary mark
   *    on an EXISTING contact) changed. A refusal stops here; nothing else is
   *    sent.
   * 2. One `POST /contacts` per added row, sequential, never stopping at a
   *    failure (`writeDraftContacts`, shared with create).
   * 3. `PATCH primaryContactId` when the mark is on a row added in step 2.
   *
   * Everything that landed is real. On a partial result the form stays open:
   * rows that saved leave the draft list (so a second Save cannot file them
   * twice), the existing list is re-read, and the report names each refused
   * row by the name typed. The caller's `onSaved` waits for the close.
   */
  /*
   * The read-back becomes the new baseline, and the local follow-up date is
   * re-synced to it: the server may have defaulted it (next cycle), and a
   * stale local value would read as an unsaved change on the next Save.
   */
  function adoptSavedCompany(saved: Company) {
    setSavedCompany(saved);
    setFollowUpOn(saved.followUpOn ?? '');
    setStageStatusReason('');
  }

  async function submitEdit(record: Company, payload: CompanyInput) {
    const rows = contactsToWrite;
    const hasPatch = Object.keys(payload).length > 0;
    if (!hasPatch && rows.length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    setEditOutcome(null);
    setNotApplied(null);
    let saved = record;
    let unapplied: string[] = [];

    if (hasPatch) {
      try {
        const result = await api.updateCompany(record.id, payload);
        saved = result.data;
        unapplied = unappliedAfterSave(payload, saved);
        adoptSavedCompany(saved);
        setPendingSaved(saved);
      } catch (caught) {
        reportSaveError(caught);
        setSubmitting(false);
        return;
      }
    }

    let contacts: ContactWriteOutcome | null = null;
    let primary: PrimaryOutcome = { kind: 'none' };
    if (rows.length > 0) {
      const outcome = await writeDraftContacts(record.id, rows);
      contacts = outcome;
      primary = await setPrimaryContact(record.id, rows, outcome);
      if (primary.kind === 'set') {
        saved = primary.company;
        adoptSavedCompany(saved);
      }
      if (outcome.saved > 0 || primary.kind === 'set') setPendingSaved(saved);

      // Landed rows leave the drafts; a landed row that carried the mark keeps
      // it under its new id, so a refused designation can be retried.
      setDraftContacts((prev) => prev.filter((row) => outcome.idByKey[row.key] === undefined));
      const markedId = primaryKey === null ? undefined : outcome.idByKey[primaryKey];
      if (markedId !== undefined) setPrimaryKey(existingContactKey(markedId));
      setContactsReload((n) => n + 1);
    }

    if (unapplied.length > 0) {
      const list = joinList(unapplied);
      const verb = unapplied.length === 1 ? 'was' : 'were';
      const pronoun = unapplied.length === 1 ? 'it' : 'them';
      setNotApplied(`The company was saved, but ${list} ${verb} not applied. Set ${pronoun} again here.`);
    }

    const partial =
      unapplied.length > 0 ||
      (contacts !== null && contacts.failures.length > 0) ||
      primary.kind === 'contact-failed' ||
      primary.kind === 'refused';

    if (partial) {
      setEditOutcome({ contacts, primary });
      setSubmitting(false);
      return;
    }

    onSaved(saved);
    onClose();
  }

  /** Close, handing the caller anything an edit already saved. */
  function handleClose() {
    if (pendingSaved !== null) onSaved(pendingSaved);
    if (created !== null && !createdSavedFired.current) {
      createdSavedFired.current = true;
      // The primary PATCH's read-back is the freshest copy; the `201`'s
      // approval round rides along on it.
      onSaved(
        primaryOutcome.kind === 'set'
          ? { ...primaryOutcome.company, approvalRequest: created.approvalRequest }
          : created,
      );
    }
    onClose();
  }

  /*
   * The window turns into the confirmation once the create and its contacts
   * have settled (owner, 2026-10-16). While contacts are still being written
   * the form stays, with Save showing its spinner.
   */
  const showCreatedConfirmation = !isEdit && created !== null && !submitting;

  function updateDraftContact(key: string, patch: DraftContactPatch) {
    // The row as rendered, to tell which fields this edit actually changes: a
    // name edit can fill the other two (`applyNameEdit`, both ways), and an
    // auto-filled field's error is as stale as the typed one's.
    const before = draftContacts.find((row) => row.key === key);
    const after = before === undefined ? undefined : applyDraftContactPatch(before, patch);
    setDraftContacts((prev) =>
      prev.map((row) => (row.key === key ? applyDraftContactPatch(row, patch) : row)),
    );
    // An error about a value the user has since changed is noise, not guidance —
    // the same rule `setField` follows, applied per row rather than per form.
    setContactErrors((prev) => {
      const rowErrors = prev[key];
      if (rowErrors === undefined) return prev;
      const nextRow = { ...rowErrors };
      let changed = false;
      for (const field of ['firstName', 'name', 'email'] as const) {
        const touched =
          field in patch ||
          (before !== undefined && after !== undefined && before[field] !== after[field]);
        if (touched && field in nextRow) {
          delete nextRow[field];
          changed = true;
        }
      }
      if (!changed) return prev;
      const next = { ...prev };
      if (Object.keys(nextRow).length === 0) delete next[key];
      else next[key] = nextRow;
      return next;
    });
  }

  function removeDraftContact(key: string) {
    setDraftContacts((prev) => prev.filter((row) => row.key !== key));
    setContactErrors((prev) => (key in prev ? without(prev, key) : prev));
    /*
     * ⚠️ Clearing the mark with the row is the whole reason primary is tracked
     * by draft KEY rather than by array index. Removing the marked row must
     * leave nobody marked — under an index it would silently slide onto
     * whoever moved up into that position, with nothing on screen changing.
     */
    // On edit the mark falls back to the STORED primary, never to "nobody":
    // removing a draft must not quietly clear an existing designation.
    setPrimaryKey((prev) => (prev === key ? baselinePrimaryKey : prev));
  }

  /*
   * ⚠️ **`COMPANY_ADDRESS_PAIRS`, so the country is NOT copied.** A company has
   * no free-text billing country to copy FROM any more, and `shippingCountry`
   * is left exactly as the reader typed it rather than being blanked by a
   * button labelled "same as billing" — copying `''` over a real value is the
   * silent destruction this control exists to make explicit.
   */
  function copyBillingToShipping() {
    setValues((prev) => {
      const next = { ...prev };
      for (const [from, to] of COMPANY_ADDRESS_PAIRS) next[to] = prev[from];
      return next;
    });
    setCopiedAddress(true);
  }

  const billingIsBlank = COMPANY_ADDRESS_PAIRS.every(([from]) => values[from].trim() === '');
  const shippingMatchesBilling = COMPANY_ADDRESS_PAIRS.every(
    ([from, to]) => values[from] === values[to],
  );

  const textProps = (key: TextKey) => ({
    fieldKey: key,
    value: values[key],
    error: errors[key],
    onChange: setField,
  });

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit
    ? !isDirty && !primaryChanged && contactsToWrite.length === 0
    : values.name.trim() === '';

  if (showCreatedConfirmation) {
    return (
      <Modal
        open
        onClose={handleClose}
        size="lg"
        dismissOnBackdrop={false}
        title="New company"
        footer={
          <Button variant="primary" onClick={handleClose}>
            {createdContinueLabel}
          </Button>
        }
      >
        <div className="space-y-4">
          <CreatedReport
            company={created}
            contacts={contactOutcome}
            primary={primaryOutcome}
          />
          {/* Warning-toned: the company is real, and only part of it was applied. */}
          {notApplied !== null && (
            <Notice tone="warning" role="alert" className="break-words">
              {notApplied}
            </Notice>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={handleClose}
      size="lg"
      dismissOnBackdrop={false}
      initialFocusRef={nameRef}
      title={company ? `Edit ${company.name}` : 'New company'}
      footer={
        <>
          {/*
            "No changes yet." stood here and is GONE — owner's stricter
            helper-text ruling, 2026-09-05. Save is still disabled on `!isDirty`
            (an empty PATCH is a 422), so the CONTROL is unchanged; only the
            narration went.
          */}
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            {/* "Cancel" would be a lie once the company exists — there is
                nothing left to cancel, only a report left to read. The same
                holds once an edit has saved part of what was asked. */}
            {created !== null || pendingSaved !== null ? 'Close' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            /*
             * ⚠️ `created` latches this off for good. Once the company exists,
             * this button can only ever file a SECOND one — it retries nothing,
             * because the contacts and the primary designation are separate
             * requests the report below reports on individually.
             */
            /*
             * An EDIT is not latched by `notApplied`: it diffs against the
             * read-back (`savedCompany`), so pressing Save again re-sends only
             * what did not land — which is what that notice asks for.
             */
            disabled={
              saveDisabled || (!isEdit && notApplied !== null) || created !== null
            }
          >
            {isEdit ? 'Save changes' : 'Create company'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        {/*
          * Warning-toned, not danger: the write SUCCEEDED and the record is
          * real. Colouring it as a failure would send someone looking for a
          * company that exists, or worse, creating it a second time.
          */}
        {notApplied !== null && (
          <Notice tone="warning" role="alert" className="break-words">
            {notApplied}
          </Notice>
        )}

        {/*
          The shared summary rather than a second copy of its wording: the rule
          that only routable errors may be counted lives in one place, and a
          form message and a count now render together instead of the message
          silently swallowing the count.
        */}
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        {/* A create's report now lives on the confirmation view above. */}

        <EditContactsReport outcome={editOutcome} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              nameRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label={COMPANY_NAME_LABEL} required error={errors.name}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.name}
                  maxLength={MAX_LENGTHS.name}
                  autoComplete="organization"
                  invalid={Boolean(errors.name)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('name', event.target.value)}
                />
              )}
            </Field>
            <SimilarCompanies matches={similarMatches} lookupFailed={similarLookupFailed} />
          </div>

          <div className="sm:col-span-2">
            <Field label={BILLING_NAME_LABEL} required error={errors.billingName}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.billingName}
                  maxLength={MAX_LENGTHS.billingName}
                  autoComplete="off"
                  invalid={Boolean(errors.billingName)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('billingName', event.target.value)}
                />
              )}
            </Field>
          </div>

          {/*
           * ⚠️ At the START of the form, beside the name, on the owner's
           * instruction — and the position is doing real work rather than
           * following a preference. This is the field the server derives the
           * company's billing region from, and the region decides the currency
           * and the Xero tax and branding defaults that appear on the approval
           * page and in the approval email. Somebody who scrolls past it files a
           * school that will be quoted in the wrong currency.
           */}
          <CountryField
            value={values.countryId}
            error={errors.countryId}
            rows={countries.rows}
            loading={countries.loading}
            stored={company?.country ?? null}
            onChange={(next) => setField('countryId', next)}
          />

          <StageField
            value={values.stageId}
            error={errors.stageId}
            records={stages.records}
            loading={stages.loading}
            stored={company?.stage ?? null}
            onChange={(next) => setField('stageId', next)}
          />

          <StageStatusField
            value={values.stageStatusId}
            error={errors.stageStatusId}
            stageId={values.stageId}
            stageName={
              stages.records.find((stage) => String(stage.id) === values.stageId)?.name ??
              (company?.stage && String(company.stage.id) === values.stageId
                ? company.stage.name
                : null)
            }
            records={stageStatuses.statuses}
            loading={stageStatuses.loading}
            failed={stageStatuses.error !== null}
            stored={
              company?.stageStatus && values.stageId === baseline.stageId
                ? company.stageStatus
                : null
            }
            onChange={changeStageStatus}
          />

          {followUpMode !== null && (
            <div className="sm:col-span-2">
              <Field
                label="Follow up on"
                required={followUpMode === 'date'}
                error={errors.followUpOn}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <div className="grid gap-2">
                    {followUpMode === 'date' && (
                      <div className="flex flex-wrap gap-2" role="group" aria-label="Quick picks">
                        {FOLLOW_UP_QUICK_PICKS.map((pick) => {
                          const pickValue = addMonthsIso(followUpToday, pick.months);
                          const active = followUpOn === pickValue;
                          return (
                            <Button
                              key={pick.months}
                              type="button"
                              variant={active ? 'primary' : 'secondary'}
                              aria-pressed={active}
                              onClick={() => changeFollowUpOn(pickValue)}
                            >
                              {pick.label}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                    <Input
                      id={id}
                      type="date"
                      value={followUpOn}
                      min={followUpMin}
                      max={followUpMax}
                      required={followUpMode === 'date'}
                      invalid={Boolean(errors.followUpOn)}
                      aria-describedby={describedBy}
                      onChange={(event) => changeFollowUpOn(event.target.value)}
                    />
                  </div>
                )}
              </Field>
            </div>
          )}

          {asksReason && (
            <div className="sm:col-span-2">
              <Field label="Reason" required error={errors.stageStatusReason}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Textarea
                    id={id}
                    rows={3}
                    value={stageStatusReason}
                    maxLength={STAGE_STATUS_REASON_MAX}
                    required
                    invalid={Boolean(errors.stageStatusReason)}
                    aria-describedby={describedBy}
                    onChange={(event) => changeStageStatusReason(event.target.value)}
                  />
                )}
              </Field>
            </div>
          )}

          {/* A destructive-consequence sentence (allowed), not helper text. */}
          {warnsMarketing && (
            <Notice tone="warning" className="sm:col-span-2">
              Its contacts will stop receiving marketing emails.
            </Notice>
          )}

          {/*
           * ⚠️ In the top grid beside Stage and Country, on the owner's
           * decision — NOT behind "More details". Where an account came from is
           * asked once, when the school is filed, by the person filing it; a
           * fold is where a field goes to be filled in later, and "later" for
           * this one is never, because nobody re-opens a company to record how
           * they first heard of it.
           *
           * ⚠️ It is OPTIONAL and blank is ordinary — see `toFormValues`. The
           * control makes no attempt to chase a value.
           */}
          <SourceField
            value={values.sourceId}
            error={errors.sourceId}
            records={sources.records}
            loading={sources.loading}
            stored={company?.source ?? null}
            onChange={(next) => setField('sourceId', next)}
          />

          {/*
           * Beside Source, in the same top grid and on the same reasoning: what
           * kind of organisation this is, is answered once by the person filing
           * the school. ⚠️ **Two adjacent pickers whose labels must not
           * converge** — "Company source" and "Company type" are different
           * questions, and a reader who reads them as one control with two names
           * will fill in whichever they meet first.
           *
           * ⚠️ Optional, and blank is ordinary — see `toFormValues`. The control
           * makes no attempt to chase a value.
           */}
          <CompanyTypeField
            value={values.companyTypeId}
            error={errors.companyTypeId}
            records={companyTypes.records}
            loading={companyTypes.loading}
            stored={company?.companyType ?? null}
            onChange={(next) => setField('companyTypeId', next)}
          />

          {/*
           * The Owner picker stood here and is GONE, not hidden — see the
           * `StaffKey` union for why removing the control without removing the
           * key would have cleared a live column on an unrelated edit.
           */}
          <StaffField
            label="Account lead"
            value={values.accountLeadId}
            error={errors.accountLeadId}
            users={leadStaff.users}
            loading={usersLoading}
            current={company?.accountLead ?? null}
            /*
             * 🔴 **TWO DIFFERENT ROW SETS ON THE TWO PATHS, because the blank
             * row means two different things and no single label is true of
             * both.** Owner's decision 2026-09-18.
             *
             *   CREATE  blank = "You — <name>"  -> key omitted, server assigns
             *                                     the person creating it
             *           plus   "No account lead" -> explicit null, nobody
             *
             *   EDIT    blank = "Unassigned"     -> explicit null, nobody
             *           and NO extra row, because the blank already says it
             *
             * ⚠️ **The edit side is deliberately UNTOUCHED.** It was never
             * wrong: there is no creator default on `PATCH /companies`, so a
             * blank there really is an absence. Giving it the extra row too
             * would put two rows with one outcome in front of the people who
             * use this screen most.
             */
            emptyLabel={isEdit ? UNASSIGNED_LABEL : creatorLeadLabel(signedInUser)}
            extraOption={isEdit ? null : NO_ACCOUNT_LEAD_OPTION}
            groupNote={narrowingNote(leadStaff)}
            outsideNote={outsideGroupNote(leadStaff)}
            onChange={(next) => {
              // The ONLY writer of this ref. `next` may be '' — clearing the
              // seeded lead is a choice, and it has to stop the seed exactly as
              // choosing somebody else does.
              leadTouched.current = true;
              setField('accountLeadId', next);
            }}
          />
          <StaffField
            label="Client success manager"
            value={values.clientSuccessManagerId}
            error={errors.clientSuccessManagerId}
            users={csmStaff.users}
            loading={usersLoading}
            current={company?.clientSuccessManager ?? null}
            groupNote={narrowingNote(csmStaff)}
            outsideNote={outsideGroupNote(csmStaff)}
            onChange={(next) => setField('clientSuccessManagerId', next)}
          />
          {/*
           * Email, Mobile and Website stood here, loose in the unlabelled top
           * grid, and have MOVED into the "Contact details" section below —
           * where their three siblings (`secondaryEmail`, `alternateMobile`,
           * `linkedin`) already were, behind "More details". Owner's request,
           * and it ends an account's contact details being split across two
           * folds. See `SECTION_FIELDS.contactDetails` for the error-routing
           * half, which is what makes the move safe rather than merely tidy.
           */}
        </div>

        {/*
         * *SUPERSEDED 2026-10-15: "⚠️ CREATE ONLY. On edit the company's real
         * contacts live on its detail page."* Reported as a bug: editing from
         * the companies list showed the company's own email/phone columns
         * ("Contact details", below) and no person at all, with no way to add
         * one. On EDIT this section now lists the existing contacts (name,
         * email, phone, group, primary mark) and adds new ones through the same
         * draft rows as create. Editing an existing contact still happens on
         * the contact itself.
         *
         * ⚠️ **It sits ABOVE "Contact details" and is PERMANENTLY OPEN** —
         * owner's request: the people at the account are what a person is here
         * to type, and the company's own email and phone columns come after
         * them. `collapsible={false}` renders no chevron and no button rather
         * than a control that toggles nothing, because an affordance with one
         * outcome should be absent rather than inert.
         *
         * ⚠️ **So there is no `SECTION_FIELDS` entry and no `openSections` key
         * for it, and that is correct rather than an oversight.** The rows here
         * are draft CONTACTS, not company columns: nothing in them is ever a
         * key on the `POST /companies` body, so no `details[].path` from that
         * request can ever name one and there is nothing for
         * `revealSectionsWithErrors` to match. Its errors are client-side and
         * per row, and they are on screen already because nothing folds them
         * away. **Do not "fix" this by inventing paths like `contacts.0.name`**:
         * the server never sends them, so the entry would match nothing while
         * looking like it worked.
         */}
        <Section
          collapsible={false}
          title="Contacts"
          summary={
            contactsToWrite.length === 0
              ? ''
              : contactsToWrite.length === 1
                ? '1 to be created'
                : `${contactsToWrite.length} to be created`
          }
          errorCount={Object.keys(contactErrors).length}
        >
          <div className="grid gap-3">
            {company && (
              <ExistingContactsList
                rows={existingContacts.rows}
                loading={existingContacts.loading}
                failed={existingContacts.failed}
                storedPrimary={current?.primaryContact ?? null}
                primaryKey={primaryKey}
                disabled={submitting}
                radioName={`${formId}-primary-contact`}
                onPrimaryChange={setPrimaryKey}
              />
            )}
            {canAddContacts && (
              <CompanyContactsDraft
                rows={draftContacts}
                errors={contactErrors}
                primaryKey={primaryKey}
                groups={contactGroups.rows ?? []}
                groupsLoading={contactGroups.loading}
                disabled={submitting || created !== null}
                radioName={`${formId}-primary-contact`}
                onChange={updateDraftContact}
                onAdd={() => setDraftContacts((prev) => [...prev, newDraftContact()])}
                onRemove={removeDraftContact}
                onPrimaryChange={setPrimaryKey}
              />
            )}
          </div>
        </Section>

        <Section
          id="contactDetails"
          title="Contact details"
          summary="Email, phone and links"
          open={openSections.contactDetails}
          errorCount={countErrors(errors, SECTION_FIELDS.contactDetails)}
          onToggle={() =>
            setOpenSections((prev) => ({ ...prev, contactDetails: !prev.contactDetails }))
          }
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              {...textProps('email')}
            />
            <TextField
              label="Secondary email"
              type="email"
              {...textProps('secondaryEmail')}
            />
            <TextField
              label="Mobile"
              type="tel"
              autoComplete="tel"
              {...textProps('mobile')}
            />
            <TextField label="Alternate mobile" type="tel" {...textProps('alternateMobile')} />
            <TextField
              label="Website"
              inputMode="url"
              {...textProps('website')}
            />
            <TextField
              label="LinkedIn"
              inputMode="url"
              {...textProps('linkedin')}
            />
          </div>
        </Section>

        <Section
          id="address"
          title="Address"
          summary="Billing and shipping"
          open={openSections.address}
          errorCount={countErrors(errors, SECTION_FIELDS.address)}
          onToggle={() => setOpenSections((prev) => ({ ...prev, address: !prev.address }))}
        >
          <fieldset>
            <legend className="text-xs font-semibold text-fg-secondary">
              Billing
            </legend>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <TextField label="Street" {...textProps('billingStreet')} />
              </div>
              <TextField label="City" {...textProps('billingCity')} />
              <TextField label="State" {...textProps('billingState')} />
              <TextField label="Postal code" {...textProps('billingCode')} />
              {/* ⚠️ NO Country line here, deliberately. The company's country
                  is the structured `CountryField` at the top of this form —
                  the foreign key the server derives the billing region, the
                  currency and the Xero defaults from. The free-text billing
                  country it used to sit beside is no longer collected (owner's
                  decision, 2026-08-31), and the key is absent from
                  `MAX_LENGTHS` so no payload here can express it. */}
            </div>
          </fieldset>

          <fieldset className="mt-5">
            <legend className="text-xs font-semibold text-fg-secondary">
              Shipping
            </legend>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={copyBillingToShipping}
                disabled={billingIsBlank || shippingMatchesBilling}
                leadingIcon={<CopyIcon />}
              >
                {SAME_AS_BILLING_LABEL}
              </Button>
              <span role="status" className="text-xs text-fg-muted">
                {copiedAddress && shippingMatchesBilling ? 'Copied from billing.' : ''}
              </span>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <TextField label="Street" {...textProps('shippingStreet')} />
              </div>
              <TextField label="City" {...textProps('shippingCity')} />
              <TextField label="State" {...textProps('shippingState')} />
              <TextField label="Postal code" {...textProps('shippingCode')} />
              <TextField label="Country" {...textProps('shippingCountry')} />
            </div>
          </fieldset>
        </Section>

        <Section
          id="more"
          title="More details"
          summary="Layout"
          open={openSections.more}
          errorCount={countErrors(errors, SECTION_FIELDS.more)}
          onToggle={() => setOpenSections((prev) => ({ ...prev, more: !prev.more }))}
        >
          {/*
           * Down to `layout` alone — the alternate contact fields moved to
           * "Contact details" above. Kept as a section rather than folded away
           * because `layout` is a real writable column with a real 422 path and
           * has nowhere else to sit; the summary was updated with it, since
           * "Layout and alternate contacts" now names two fields that are not
           * here.
           */}
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField label="Layout" {...textProps('layout')} />
          </div>
        </Section>

        <Section
          id="xero"
          title="Xero sales defaults"
          /*
           * A field-name peek, matching its five siblings. It read "What Xero
           * should assume when invoicing" — an explanation rather than a list of
           * what is inside, which is the one job this slot has.
           */
          summary="Line amounts and tax rate"
          open={openSections.xero}
          errorCount={countErrors(errors, SECTION_FIELDS.xero)}
          onToggle={() => setOpenSections((prev) => ({ ...prev, xero: !prev.xero }))}
        >
          <XeroSalesDefaultsFields
            lineAmountType={values.xeroSalesLineAmountType}
            taxType={values.xeroSalesTaxType}
            errors={errors}
            rates={taxRates.rates}
            ratesLoading={taxRates.loading}
            ratesError={taxRates.error}
            onReloadRates={taxRates.reload}
            connectionLoading={xero.loading}
            connected={xero.connection.connected}
            notInstalled={xero.notInstalled}
            storedTaxType={company?.xeroSalesTaxType ?? null}
            storedTaxName={company?.xeroSalesTaxName ?? null}
            cleared={clearedXeroDefaults}
            onChange={setField}
          />
        </Section>

        <Section
          id="notes"
          title="Notes"
          summary="Internal description"
          open={openSections.notes}
          errorCount={countErrors(errors, SECTION_FIELDS.notes)}
          onToggle={() => setOpenSections((prev) => ({ ...prev, notes: !prev.notes }))}
        >
          <Field label="Description" error={errors.description}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Textarea
                id={id}
                rows={5}
                value={values.description}
                maxLength={MAX_LENGTHS.description}
                invalid={Boolean(errors.description)}
                aria-describedby={describedBy}
                onChange={(event) => setField('description', event.target.value)}
              />
            )}
          </Field>
        </Section>
      </form>
    </Modal>
  );
}

function countErrors(errors: Record<string, string>, keys: readonly string[]): number {
  return keys.filter((key) => key in errors).length;
}

// Generic over the value so it serves both the flat `Record<string, string>`
// error maps and the per-row `DraftContactErrors`.
function without<T>(source: Record<string, T>, key: string): Record<string, T> {
  const next = { ...source };
  delete next[key];
  return next;
}

/*
 * `XeroNote` is GONE — owner's stricter helper-text ruling, 2026-09-05. It
 * rendered the company's Xero contact id with one of two sentences after it:
 * "linked as {id}. Set by the link, not editable here." and "not linked yet.
 * Set by linking a Xero contact."
 *
 * ⚠️ It looked like a protected Xero sentence and is not one. The protected
 * family is about a PUSH — a document or a company reaching the accountant's
 * Xero — where the cost of a wrong belief is a real customer-facing artefact.
 * This only described where a field is written from, on a field this form never
 * offered in the first place.
 *
 * ⚠️ THE GUARD IS UNTOUCHED, and it never lived in this component: `xeroContactId`
 * is absent from `FormValues` and from `MAX_LENGTHS`, so no payload this form
 * builds can express it and the 422 it would collect is unreachable. Deleting
 * the note removes a description, not a protection.
 */
/**
 * What the one "Create company" press actually did, reported as the three
 * separate facts it is.
 *
 * ⚠️ **The whole point is that "the company was created", "the contacts were
 * created" and "the primary was designated" are three outcomes of three
 * requests with no transaction across them.** A report collapsing them into one
 * "Saved" — or worse, into one "Failed" — is wrong in the case that matters:
 * the company exists, a contact does not, and the person is about to close this
 * form believing nothing happened and file the school a second time.
 *
 * ⚠️ **"Nothing was saved" after a successful `POST /companies` is a lie the
 * person acts on.** So the green block is unconditional once the company
 * exists, and every failure is rendered BESIDE it rather than instead of it.
 *
 * 🔴 2026-10-16 (owner): this is the whole body of the confirmation the New
 * company window turns into — a headline and at most one short fact line about
 * the approval round (`describeNewCompanyApproval`), Resend only on its failure
 * branches, and contact lines only when something failed. No explanations or
 * next-step advice. *SUPERSEDED: "{name} was created and is saved." plus a
 * contacts tally on every create.*
 *
 * Nothing here offers to undo anything, because nothing can. Deleting the
 * company to "roll back" a refused contact would destroy the part that worked,
 * and calling a second destructive write a rollback is worse than saying
 * plainly what stands.
 */
function CreatedReport({
  company,
  contacts,
  primary,
}: {
  company: CompanyCreateResult | null;
  contacts: ContactWriteOutcome | null;
  primary: PrimaryOutcome;
}) {
  const { ability } = useAuth();
  if (company === null) return null;

  const failures = contacts?.failures ?? [];
  // Only a pending company was submitted; anything else gets no approval line.
  const pending = isResendable(company);
  const approval = pending
    ? describeNewCompanyApproval(company.approvalRequest)
    : { tone: 'success' as const, line: null, offerResend: false };
  const canResend = approval.offerResend && resendDenialFor(ability) === null;

  return (
    <div className="grid gap-2">
      <Notice
        tone={approval.tone}
        role={approval.tone === 'success' ? 'status' : 'alert'}
        className="break-words"
      >
        <strong className="font-semibold">{newCompanyHeadline(company)}</strong>
        {approval.line !== null && <span className="block">{approval.line}</span>}
      </Notice>

      {canResend && <ResendApprovalControl company={company} variant="detail" />}

      {failures.length > 0 && (
        <Notice tone="danger" role="alert" className="break-words">
          <p>
            <strong className="font-semibold">
              {failures.length} of {contacts?.attempted}{' '}
              {contacts?.attempted === 1 ? 'contact' : 'contacts'} not saved.
            </strong>
          </p>
          {/*
            Numbered by the position each row held in the list as SUBMITTED,
            which is not its position now — the rows that landed are gone from
            the form. Said plainly rather than letting somebody count down.
          */}
          <ul className="mt-2 grid gap-1 pl-6">
            {failures.map((failure) => (
              <li key={failure.position} className="list-disc">
                <strong className="font-semibold">Contact {failure.position}</strong> —{' '}
                {failure.message}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {/*
        The third outcome, in its own band.

        ⚠️ `contact-failed` is the branch marking-primary-inline creates: the
        row somebody marked is the row that was refused, so there is no contact
        to designate. It says so and promotes NOBODY — quietly designating
        whoever else saved would put a different person at the head of the
        customer's Xero record than the one that was asked for, under a success
        message.

        Warning-toned rather than danger: the company and the surviving
        contacts are real, and only the designation is missing.
      */}
      {(primary.kind === 'contact-failed' || primary.kind === 'refused') && (
        <Notice tone="warning" role="alert" className="break-words">
          <span className="min-w-0">
            <strong className="font-semibold">Primary contact not set.</strong>{' '}
            {primary.kind === 'contact-failed'
              ? `Contact ${primary.position} was not saved.`
              : primary.message}
          </span>
        </Notice>
      )}
    </div>
  );
}

/**
 * The company's contacts on file, on the EDIT form, each with the shared
 * "Primary contact" radio (same `name` as the draft rows', so the mark is
 * exclusive across both lists).
 *
 * Read-only otherwise: editing a person is done on the contact. One stacked
 * row per contact at every width — the modal is narrow enough that a table
 * would only add a horizontal scroll.
 *
 * The stored primary is injected when the fetched list lacks it (the server's
 * read joins on the id alone), the same rule as `primaryContactField` on
 * `CompanyDetailPage`: dropping them would show nobody marked over a
 * designation that exists.
 */
function ExistingContactsList({
  rows,
  loading,
  failed,
  storedPrimary,
  primaryKey,
  disabled,
  radioName,
  onPrimaryChange,
}: {
  rows: Contact[] | null;
  loading: boolean;
  failed: string | null;
  storedPrimary: Company['primaryContact'];
  primaryKey: string | null;
  disabled: boolean;
  radioName: string;
  onPrimaryChange: (key: string | null) => void;
}) {
  if (rows === null && loading) {
    return (
      <div className="grid gap-2" aria-busy="true">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const listed: {
    id: number;
    name: string;
    email: string | null;
    phone: string | null;
    group: string | null;
  }[] = (rows ?? []).map((contact) => ({
    id: contact.id,
    name: contact.name,
    email: contact.email,
    phone: contact.phone ?? contact.mobile,
    group: contact.contactGroup?.name ?? null,
  }));
  if (storedPrimary && !listed.some((row) => row.id === storedPrimary.id)) {
    listed.unshift({
      id: storedPrimary.id,
      name: storedPrimary.name,
      email: storedPrimary.email,
      phone: null,
      group: null,
    });
  }

  return (
    <div className="grid gap-2">
      {failed !== null && (
        <Notice tone="danger" role="alert" className="break-words">
          Couldn’t load this company’s contacts. {failed}
        </Notice>
      )}
      {listed.length === 0 && failed === null ? (
        <EmptyState title="No contacts yet" className="py-4" />
      ) : (
        listed.length > 0 && (
          <ul
            className={cx(
              'divide-y divide-border rounded-md border border-border',
              loading && 'opacity-60',
            )}
          >
            {listed.map((row) => {
              const key = existingContactKey(row.id);
              const isPrimary = primaryKey === key;
              const email = row.email?.trim() ?? '';
              const phone = row.phone?.trim() ?? '';
              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium text-fg">{row.name}</span>
                      {row.group !== null && <Badge>{row.group}</Badge>}
                    </div>
                    {(email !== '' || phone !== '') && (
                      <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                        {email !== '' && <EmailLink address={email} tone="muted" />}
                        {email !== '' && phone !== '' && ' · '}
                        {phone}
                      </p>
                    )}
                  </div>
                  {/* Clickable to CLEAR, same mechanism as `CompanyContactsDraft`. */}
                  <Radio
                    name={radioName}
                    checked={isPrimary}
                    disabled={disabled}
                    label="Primary contact"
                    onChange={() => onPrimaryChange(key)}
                    onClick={() => {
                      if (isPrimary) onPrimaryChange(null);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )
      )}
    </div>
  );
}

/**
 * What an EDIT's contacts phase did, when it fell short. Nothing renders on a
 * full success — the form closes then.
 *
 * Refused rows are named by the name typed (required, so never blank) rather
 * than by position: the rows that landed have already left the draft list, so a
 * position would point at the wrong box. The refused ones stay in the form for
 * another Save.
 */
function EditContactsReport({ outcome }: { outcome: EditContactsOutcome | null }) {
  if (outcome === null) return null;
  const { contacts, primary } = outcome;
  const failures = contacts?.failures ?? [];

  return (
    <div className="grid gap-2">
      {contacts !== null && contacts.saved > 0 && (
        <Notice tone="success" role="status" className="break-words">
          {contacts.saved === 1
            ? 'One contact was added.'
            : `${contacts.saved} contacts were added.`}
        </Notice>
      )}

      {failures.length > 0 && (
        <Notice tone="danger" role="alert" className="break-words">
          <p>
            <strong className="font-semibold">
              {failures.length === 1
                ? 'One contact was refused.'
                : `${failures.length} contacts were refused.`}
            </strong>{' '}
            {failures.length === 1 ? 'It is' : 'They are'} still below; correct and save again.
          </p>
          <ul className="mt-2 grid gap-1 pl-6">
            {failures.map((failure) => (
              <li key={failure.position} className="list-disc">
                <strong className="font-semibold">{failure.name}</strong> — {failure.message}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {(primary.kind === 'contact-failed' || primary.kind === 'refused') && (
        <Notice tone="warning" role="alert" className="break-words">
          {primary.kind === 'contact-failed' ? (
            <>
              <strong className="font-semibold">No primary contact was set.</strong> The contact
              marked primary was not created, and nobody else was made primary.
            </>
          ) : (
            <>
              <strong className="font-semibold">The primary contact was not set.</strong>{' '}
              {primary.message}
            </>
          )}
        </Notice>
      )}
    </div>
  );
}

/**
 * Companies already on file under a name like the one being typed.
 *
 * ⚠️ **This block is a warning about a state the reader cannot otherwise see,
 * not helper text, and a prose sweep must leave it alone.** See the comment on
 * `similar` in `CompanyForm` for the full reasoning; the short version is that
 * company names are not unique by design, so nothing else in this app — no
 * validator, no 409, no list the user is looking at — will ever mention that
 * the school they are adding already exists.
 *
 * Three deliberate restraints:
 *
 * - **Nothing renders when the server answered and nothing matched.** No "no
 *   similar companies found": that is reassurance the check did not earn (it is
 *   a fuzzy match, not proof of absence) and it would flicker under the field on
 *   every keystroke. ⚠️ A FAILED lookup is the exception and is the one state
 *   that must not share that silence — see `lookupFailed` below.
 * - **"Looks similar to", never "duplicate", and no merge control.** Two
 *   schools under one university bill separately and are correctly two records.
 *   Naming these matches as duplicates asserts something the server did not.
 * - **`score` is deliberately not rendered.** A number invites the reader to
 *   take a fuzzy string comparison as a measurement. `reason` is authored
 *   server-side precisely so the client does not have to explain the match, so
 *   it is printed verbatim and nothing of ours is put in front of it.
 *
 * Layout: it sits under the name field in normal flow and gives focus straight
 * back. `Modal` scrolls its body independently of its footer, so however many
 * rows appear the Save button cannot be pushed off screen.
 */
function SimilarCompanies({
  matches,
  lookupFailed,
}: {
  matches: SimilarCompany[];
  lookupFailed: boolean;
}) {
  /*
   * ⚠️ The third state, and the reason it survives the no-helper-text
   * prohibition. Applying the file's own test — *what wrong conclusion does
   * this prevent?* — the answer is concrete: **that no similar company
   * exists.** Without this line a lookup that never came back renders exactly
   * the nothing a clean check renders, so the reader takes silence as an
   * all-clear it was never given. That is a state they cannot otherwise see,
   * which puts it on the exception list, not on the sweep list.
   *
   * Three things it is deliberately NOT:
   *
   * - **Not a validation error.** No `danger` tone, no border, no `invalid` on
   *   the input, and it is outside the name field's `aria-describedby`. The
   *   name is fine; our lookup is not, and colouring this like a refusal points
   *   the reader at the wrong thing.
   * - **Not a spinner, and not per-keystroke.** It renders only for a lookup
   *   that has SETTLED as failed against the name currently in the box —
   *   `settledSimilar` upstream is what enforces that.
   * - **Not advice.** It says what did not happen and stops. There is nothing
   *   useful to instruct here: the save is unaffected, the warning was advisory
   *   to begin with, and a "try again" on a debounced background lookup names
   *   no control the reader can press.
   */
  if (lookupFailed) {
    return (
      <p role="status" className="mt-2 text-xs text-fg-muted">
        Couldn’t check for similar companies.
      </p>
    );
  }

  if (matches.length === 0) return null;

  return (
    <div
      role="status"
      className="mt-2 rounded-md border border-border bg-surface-sunken px-3 py-2"
    >
      <p className="text-xs font-medium text-fg-secondary">
        {matches.length === 1
          ? 'One company on file looks similar to this name.'
          : `${matches.length} companies on file look similar to this name.`}
      </p>
      <ul className="mt-2 space-y-2">
        {matches.map((match) => (
          <SimilarCompanyRow key={match.id} match={match} />
        ))}
      </ul>
    </div>
  );
}

function SimilarCompanyRow({ match }: { match: SimilarCompany }) {
  /*
   * The full name and the short name both, never one standing in for the
   * other. Identity is being ESTABLISHED here rather than recalled: the whole
   * job of this row is to let somebody tell one HSNC college from another, and
   * two rows both reading "HSNC" look like a listing bug rather than like a
   * truncation.
   */
  /*
   * ⚠️ `stage` IS AN OBJECT — `stage?.name`, never `stage`. Passing the row
   * itself is what blanked this form in production on 2026-08-30: `matchLabel`
   * called `.trim()` on `{id, name}`, threw mid-render, and React unmounted the
   * whole app the moment a typed name matched a company that had a stage set.
   * `types.ts` declared it as a string and every reader believed the
   * declaration; see the corrected note on `SimilarCompany`.
   */
  const meta = [
    matchLabel(match.shortName),
    matchLabel(match.stage?.name ?? null),
    matchLabel(match.city),
  ].filter((part): part is string => part !== null);

  return (
    <li className="min-w-0 break-words">
      <p className="text-xs font-medium text-fg">{match.name}</p>
      {meta.length > 0 && <p className="text-2xs text-fg-muted">{meta.join(' · ')}</p>}
      {/* The server's own sentence, verbatim. Nothing of ours restates it. */}
      <p className="text-2xs text-fg-subtle">{match.reason}</p>
    </li>
  );
}

interface TextFieldProps {
  fieldKey: TextKey;
  label: string;
  value: string;
  error?: string;
  type?: 'text' | 'email' | 'tel';
  inputMode?: 'text' | 'url';
  placeholder?: string;
  autoComplete?: string;
  onChange: (key: TextKey, value: string) => void;
}

/*
 * `maxLength` comes from the shared table rather than each call site, so the
 * control and the validator can never drift apart. `type` stays `text` for
 * website and LinkedIn on purpose — the API length-checks them and nothing more.
 */
function TextField({
  fieldKey,
  label,
  value,
  error,
  type = 'text',
  inputMode,
  placeholder,
  autoComplete,
  onChange,
}: TextFieldProps) {
  return (
    <Field label={label} error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          type={type}
          inputMode={inputMode}
          value={value}
          maxLength={MAX_LENGTHS[fieldKey]}
          placeholder={placeholder}
          autoComplete={autoComplete}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={(event) => onChange(fieldKey, event.target.value)}
        />
      )}
    </Field>
  );
}

interface StageFieldProps {
  value: string;
  error?: string;
  records: ReferenceRecord[];
  loading: boolean;
  /*
   * ⚠️ No `failed`. The master's read-failure was reported to the reader and is
   * not any more (owner's stricter helper-text ruling, 2026-09-05), so the flag
   * has no consumer here — `stages.failed` is still tracked by the hook, it is
   * simply not rendered. Do not re-add the prop without a sentence that clears
   * the bar; an unread prop is how a dead flag survives a sweep.
   */
  /** The stage on the record being edited, or null when creating. */
  stored: MasterRef | null;
  onChange: (value: string) => void;
}

/**
 * The pipeline stage — Lead / Prospect / Client / Past Client today, and
 * whatever the master holds tomorrow.
 *
 * Two things here are correctness rather than polish:
 *
 * - **"Not yet classified" is offered, not implied.** `stage` is nullable and
 *   null is a real answer, so the empty choice is a labelled option the user can
 *   return to, and it is where a new company starts. A picker that quietly
 *   preselected Lead would put a claim on the record that nobody made.
 * - **The stored value is injected via `withStoredOption()`.** The list is
 *   fetched `isActive: true`, so a stage retired after this company was filed
 *   would otherwise be missing from the options — the control would render blank
 *   and the first move of any other field would look like the user had cleared
 *   the stage. Injected, it shows as itself and is marked no longer offered.
 */
function StageField({ value, error, records, loading, stored, onChange }: StageFieldProps) {
  const options = useMemo(() => withStoredOption(records, stored), [records, stored]);

  /*
   * ⚠️ The load-failure sentence is GONE — owner's stricter helper-text ruling,
   * 2026-09-05. It read: "Stages couldn't be loaded — a stage chosen now can't
   * be checked." Only a server refusal survives the bar now, and a failed master
   * read is not one.
   *
   * ⚠️ The BEHAVIOUR it sat beside is untouched and is the half that mattered:
   * `withStoredOption` still injects the record's own stage, so an unread list
   * cannot blank the control and silently clear the field on the next save.
   */

  return (
    <Field label="Stage" error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={value}
          options={options}
          placeholder={loading ? 'Loading stages…' : 'Not yet classified'}
          emptyOptionLabel="Not yet classified"
          searchPlaceholder="Search stages…"
          loading={loading && options.length === 0}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={onChange}
        />
      )}
    </Field>
  );
}

interface StageStatusFieldProps {
  value: string;
  error?: string;
  /** The form's CURRENT stage pick ('' = none), not the stored one. */
  stageId: string;
  /** The picked stage's name, for the `{Stage} status` label; null while unknown. */
  stageName: string | null;
  records: CompanyStageStatus[];
  loading: boolean;
  /** The status master failed to load: the field shows, disabled, with its refusal. */
  failed: boolean;
  /** The record's stored status, only while the form still holds the stored stage. */
  stored: MasterRef | null;
  onChange: (value: string) => void;
}

/**
 * The drill-down under Stage (owner 2026-10-23; per-stage label 2026-10-25).
 * Labelled `{Stage name} status` and offers only the ACTIVE statuses of the
 * stage picked in this form, in `sortOrder` (then name, then id); the stored
 * value stays visible even when retired, and is dropped from view the moment
 * Stage moves (the value itself is cleared by `setField`). Hidden while no
 * stage is picked or the loaded master gives the picked stage no statuses —
 * unless an error is bound to it, so a server refusal is never swallowed.
 */
function StageStatusField({
  value,
  error,
  stageId,
  stageName,
  records,
  loading,
  failed,
  stored,
  onChange,
}: StageStatusFieldProps) {
  const options = useMemo<ComboboxOption[]>(() => {
    if (stageId === '') return [];
    const list = filterStatusesForStage(records, Number(stageId), {
      includeInactiveId: stored?.id ?? null,
    })
      .slice()
      .sort(
        (a, b) =>
          a.sortOrder - b.sortOrder || a.name.localeCompare(b.name) || a.id - b.id,
      )
      .map((status) => ({
      value: String(status.id),
      label: status.isActive ? status.name : `${status.name} (retired)`,
    }));
    if (stored && !list.some((option) => option.value === String(stored.id))) {
      list.unshift({ value: String(stored.id), label: stored.name });
    }
    return list;
  }, [records, stageId, stored]);

  const noStage = stageId === '';
  const label = stageName ? `${stageName} status` : 'Stage status';

  /*
   * Hidden only when no stage is picked, or the LOADED master says the picked
   * stage has no statuses. While loading it shows the Loading… state; when the
   * master failed it shows disabled with its refusal, never vanishes.
   */
  if (
    options.length === 0 &&
    value === '' &&
    !error &&
    (noStage || (!loading && !failed))
  ) {
    return null;
  }

  const disabledReason = noStage
    ? 'Pick a stage first.'
    : failed
      ? `${label} list unavailable — can’t be changed here.`
      : undefined;

  return (
    <Field label={label} error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <span title={disabledReason} className="block">
          <Combobox
            id={id}
            value={value}
            options={options}
            placeholder={loading && !noStage ? 'Loading…' : '—'}
            emptyOptionLabel="—"
            searchPlaceholder="Search statuses…"
            loading={loading && !noStage && options.length === 0}
            disabled={noStage || failed}
            invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={onChange}
          />
        </span>
      )}
    </Field>
  );
}

interface SourceFieldProps {
  value: string;
  error?: string;
  records: ReferenceRecord[];
  loading: boolean;
  /*
   * ⚠️ No `failed`, and the reason is `StageField`'s rather than an omission:
   * the master's read-failure is not narrated on this form (owner's stricter
   * helper-text ruling, 2026-09-05), so the flag would have no consumer.
   * `sources.failed` is still tracked by the hook and simply not rendered.
   *
   * The BEHAVIOUR that mattered is elsewhere and is untouched:
   * `companySourceOptions` injects the record's own source, so an unread master
   * cannot blank the control and have the next save read as a deliberate clear.
   */
  /** The source on the record being edited, or null when creating. */
  stored: MasterRef | null;
  onChange: (value: string) => void;
}

/**
 * Where this account came from — Organic / Conferences / Referral / Website /
 * Social Media today, and whatever the master holds tomorrow.
 *
 * ⚠️ **"No source recorded" is the empty option, and the wording is the
 * decision.** "None" reads as an answer somebody gave — a school that came from
 * nowhere — where this column's blank means only that nobody wrote it down. All
 * 413 existing companies are blank, so the wrong word here would put a false
 * claim on every one of them and on every new company until somebody moved the
 * picker.
 *
 * ⚠️ **The stored value is injected by `companySourceOptions`.** A source
 * retired after this company was filed would otherwise be missing from the
 * options, the control would render blank, and the first move of any OTHER
 * field would look exactly as though the user had cleared it.
 */
function SourceField({ value, error, records, loading, stored, onChange }: SourceFieldProps) {
  const options = useMemo(() => companySourceOptions(records, stored), [records, stored]);

  return (
    <Field label="Company source" error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={value}
          options={options}
          placeholder={loading ? 'Loading sources…' : 'No source recorded'}
          emptyOptionLabel="No source recorded"
          searchPlaceholder="Search sources…"
          loading={loading && options.length === 0}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={onChange}
        />
      )}
    </Field>
  );
}

interface CompanyTypeFieldProps {
  value: string;
  error?: string;
  records: ReferenceRecord[];
  loading: boolean;
  /*
   * ⚠️ No `failed`, for `SourceField`'s reason rather than an omission: the
   * master's read-failure is not narrated on this form (owner's stricter
   * helper-text ruling, 2026-09-05), so the flag would have no consumer.
   * `companyTypes.failed` is still tracked by the hook and simply not rendered.
   *
   * The BEHAVIOUR that mattered is elsewhere and is untouched:
   * `companyTypeOptions` injects the record's own type, so an unread master
   * cannot blank the control and have the next save read as a deliberate clear.
   */
  /** The type on the record being edited, or null when creating. */
  stored: MasterRef | null;
  onChange: (value: string) => void;
}

/**
 * What kind of organisation this is — `Institution` / `Partner` today, and
 * whatever the master holds tomorrow.
 *
 * ⚠️ **"No type recorded" is the empty option, and the wording is the
 * decision.** "None" reads as an answer somebody gave — an organisation of no
 * kind — where this column's blank means only that nobody classified it. Every
 * existing company is blank, so the wrong word here would put a false claim on
 * all of them and on every new company until somebody moved the picker.
 *
 * ⛔ **The empty option is never `Institution`.** It is seeded first and is what
 * most accounts are, which is exactly why preselecting it would be invisible.
 *
 * ⚠️ **The stored value is injected by `companyTypeOptions`.** A type retired
 * after this company was filed would otherwise be missing from the options, the
 * control would render blank, and the first move of any OTHER field would look
 * exactly as though the user had cleared it.
 */
function CompanyTypeField({
  value,
  error,
  records,
  loading,
  stored,
  onChange,
}: CompanyTypeFieldProps) {
  const options = useMemo(() => companyTypeOptions(records, stored), [records, stored]);

  return (
    <Field label="Company type" error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={value}
          options={options}
          placeholder={loading ? 'Loading types…' : 'No type recorded'}
          emptyOptionLabel="No type recorded"
          searchPlaceholder="Search types…"
          loading={loading && options.length === 0}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={onChange}
        />
      )}
    </Field>
  );
}

interface CountryFieldProps {
  value: string;
  error?: string;
  /**
   * `null` means the master was never read — NOT that it is empty.
   *
   * ⚠️ Still load-bearing even though the failure is no longer NARRATED: `rows ??
   * []` below yields an empty option list either way, and the stored country is
   * injected on top of it, which is what keeps an unread master from blanking
   * the control.
   */
  rows: Country[] | null;
  loading: boolean;
  /*
   * ⚠️ No `failed`. It fed a hint and a footnote, both removed under the owner's
   * stricter helper-text ruling (2026-09-05), and nothing else consulted it.
   */
  /** The country on the record being edited, or null when creating. */
  stored: Company['country'];
  onChange: (value: string) => void;
}

/**
 * The company's country — the STRUCTURED one, which drives billing.
 *
 * ⚠️ **THE ONLY country control on this form — 2026-08-31.** *SUPERSEDED:
 * "This is not `billingCountry`, and the two now both exist on this form … The
 * labels are deliberately 'Country' here and 'Country' under an explicit
 * **Billing** legend there."* The free-text billing country was removed from
 * the Address section on the owner's decision: a company held the same fact
 * twice, the two disagreed on 13 of 149 records, and the foreign key was right
 * every time. So there is no longer a second "Country" to distinguish this one
 * from, and the label needs no qualifier.
 *
 * This is a foreign key into the countries master, and the server derives the
 * company's billing region from it — which decides the currency
 * (GBP / EUR / USD) and the Xero tax and branding defaults that appear on the
 * approval page and in the approval email.
 *
 * ⚠️ **`shippingCountry` is still free text and is untouched.** Shipping has no
 * foreign key of its own, so the Address section still carries one Country
 * line — under the **Shipping** legend, never the billing one.
 *
 * ⚠️ **No derived currency is shown.** The mapping country → region → currency
 * is the server's, and rendering a preview of it here would be a second
 * implementation that agrees on the day it is written and drifts silently
 * afterwards — with the drift showing up as a price on a customer-facing
 * document. The saved company carries the answer; this control asks the
 * question and stops.
 *
 * 195 rows arrive in one page, so the Combobox filters them locally and there
 * is no `hasMore` to report.
 */
function CountryField({ value, error, rows, loading, stored, onChange }: CountryFieldProps) {
  const options = useMemo<ComboboxOption[]>(() => {
    // Annotated rather than inferred: the map emits no `hint`, so an inferred
    // element type would refuse the injected stored option below, which carries
    // one.
    const list: ComboboxOption[] = (rows ?? []).map((row) => ({
      value: String(row.id),
      label: row.countryName,
    }));

    /*
     * The record's own country, injected when the active list does not carry
     * it — a country retired after this company was filed would otherwise leave
     * the control blank, and the first edit of any other field would read as
     * the user having cleared it.
     *
     * ⚠️ The label is read DEFENSIVELY. `CompanyCountryRef`'s field set is the
     * one part of this contract taken from a description rather than measured,
     * so an embed spelling its label differently would render `undefined` here
     * — a confident blank in a value slot. `Country #id` is the honest fallback:
     * it names the row without claiming to know what it is called.
     */
    if (stored && !list.some((option) => option.value === String(stored.id))) {
      /*
       * ⚠️ `stored.name` — CORRECTED 2026-08-31. This read `stored.countryName`,
       * which the server has never sent: its mapper returns `{id, name, code,
       * billingRegion}`. The comment above called that risk exactly right, and
       * the `typeof` guard is why it degraded to `Country #id` instead of
       * printing `undefined` in a value slot.
       *
       * ⚠️ **The guard STAYS even though the field name is now measured.** It is
       * not redundancy: `country` is `null` on all 148 companies today, because
       * `country_id` was added without a backfill — so this branch has never
       * once run against a populated embed, and the first row to carry one will
       * be the first real exercise of it. A measured contract and an exercised
       * one are different things.
       */
      const name = typeof stored.name === 'string' ? stored.name.trim() : '';
      list.unshift({
        value: String(stored.id),
        label: name !== '' ? name : `Country #${stored.id}`,
        hint: 'No longer offered',
      });
    }

    return list;
  }, [rows, stored]);

  /*
   * ⚠️ BOTH failure sentences are GONE — owner's stricter helper-text ruling,
   * 2026-09-05. The `Field` hint read "Countries couldn't be loaded — the list
   * is unread, not empty." and the `Combobox` footnote read "Countries could not
   * be loaded." Neither is a server refusal.
   *
   * ⚠️ The distinction they drew is still REAL in the data — `rows === null` is
   * an unread master and `[]` is an empty one — and it still decides behaviour
   * above, where the stored country is injected. It is simply no longer said.
   */

  return (
    <Field label="Country" error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={value}
          options={options}
          placeholder={loading ? 'Loading countries…' : 'Not set'}
          emptyOptionLabel="Not set"
          searchPlaceholder="Search countries…"
          loading={loading && options.length === 0}
          // Not disabled on failure: the record's own country is still injected
          // above, so keeping or clearing it is the one thing that still works.
          disabled={loading}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={onChange}
        />
      )}
    </Field>
  );
}

interface StaffFieldProps {
  label: string;
  value: string;
  error?: string;
  /**
   * The people this picker may offer — already narrowed to the user group that
   * governs the field. Both remaining call sites pass a narrowed list, and both
   * are enforced server-side on `/companies`.
   */
  users: UserSummary[];
  loading: boolean;
  /*
   * ⚠️ No `failed` and no `hasMore`. They carried "Staff could not be loaded."
   * and "Showing the first 100 users." — a load failure and a truncation notice,
   * both removed under the owner's stricter helper-text ruling (2026-09-05).
   * Neither flag had any other consumer.
   *
   * ⚠️ `groupNote` below is NOT the same kind of sentence and stays: naming
   * somebody outside the group is a live 422 from `/companies`, so it is a
   * refusal, which is an explicit keeper.
   */
  /** The record's current holder, in case they fall outside the first page of users. */
  current: UserSummary | null;
  /**
   * What the blank row says. Defaults to **"Unassigned"**, which is true
   * wherever a blank genuinely means nobody — every mount of this component
   * except one.
   *
   * ⚠️ **It is a prop rather than a constant because the blank row does not
   * mean the same thing on both paths of the same picker.** On a company CREATE
   * an omitted `accountLeadId` makes the CREATOR the lead, so "Unassigned"
   * there is a false statement about what the server is about to do — the
   * defect this pair of props was added to end (2026-09-18).
   */
  emptyLabel?: string;
  /**
   * One extra row, pinned ABOVE the staff, or `null` for none.
   *
   * ⛔ **Never give a picker a row that means nothing to it.** The Client
   * success manager has no server-side default, so its blank already says
   * everything there is to say and it passes neither of these props — a second
   * absence-shaped row there would be two choices with one outcome.
   *
   * ⚠️ Its `value` is the CALLER's sentinel and this component never
   * interprets it: it goes out through `onChange` exactly as it came in, and
   * `buildPayload` is the one place it is translated.
   */
  extraOption?: ComboboxOption | null;
  /**
   * What a narrowed picker says about itself, or `null` when nothing narrows it.
   *
   * Day one this reads "Only the 1 person in Account lead can be named here",
   * which is what stops a one-name dropdown reading as a broken one.
   */
  groupNote?: string | null;
  /**
   * The marker for a stored holder the group does not contain, or `null`
   * meaning **say nothing**. `outsideGroupNote` suppresses it while the list is
   * unknown; see its own note.
   */
  outsideNote?: string | null;
  onChange: (value: string) => void;
}

/*
 * Filtering is local: `useUserOptions` already holds the whole page in memory,
 * the staff directory is small, and a round trip per keystroke would be slower
 * than the array scan it replaces. So no `onSearch` — the Combobox filters
 * `options` itself. Email rides along as the hint, since two staff sharing a
 * name are otherwise indistinguishable in the list.
 */
function StaffField({
  label,
  value,
  error,
  users,
  loading,
  current,
  emptyLabel = UNASSIGNED_LABEL,
  extraOption = null,
  groupNote = null,
  outsideNote = null,
  onChange,
}: StaffFieldProps) {
  // A staff picker that silently dropped the current assignee would read as
  // "Unassigned" and quietly clear the field on the next save. That matters
  // more now the list is filterable: the holder must survive a search that
  // doesn't match them just as it survives a truncated page.
  //
  // ⚠️ And it matters more again now the list is NARROWED: a company may hold a
  // lead who is not in the group, and this injection is the whole reason that
  // reads as a kept value rather than as an empty field waiting to be filled by
  // somebody else. The marker is `outsideNote`, which is null while the list is
  // unknown — never accuse a valid lead on the strength of a list that has not
  // arrived.
  const options = useMemo<ComboboxOption[]>(() => {
    /*
     * ⚠️ **Annotated, not inferred.** The map alone infers `hint: string`, so an
     * option whose `hint` is optional — every caller-supplied one — is refused
     * by `unshift` below. It is a WIDENING to the type this memo already
     * returns, never a cast: `tsc` still checks each literal against it, and it
     * is what the sibling options memo higher in this file does for the same
     * reason. `tsc` caught this, not a browser.
     */
    const list: ComboboxOption[] = users.map((user) => ({
      value: String(user.id),
      label: user.name,
      hint: user.email,
    }));
    if (current && !users.some((user) => user.id === current.id)) {
      const hint = [current.email, outsideNote].filter(Boolean).join(' · ');
      list.unshift({ value: String(current.id), label: current.name, hint });
    }
    /*
     * Pinned to the very top, above the injected holder — the two choices that
     * name NOBODY read together, and the people read together beneath them.
     *
     * ⚠️ **The two cannot both appear today**, which is why the order between
     * them is not a decision anybody has had to take: `current` is the stored
     * holder and is `null` on a create, and the create is the only mount that
     * passes an extra row. Stated rather than left to be rediscovered by
     * whoever first gives an edit one.
     */
    if (extraOption) list.unshift(extraOption);
    return list;
  }, [users, current, outsideNote, extraOption]);

  /*
   * ⚠️ Down to the GROUP NOTE alone — owner's stricter helper-text ruling,
   * 2026-09-05. Two of the three facts are gone: "Staff could not be loaded."
   * (a load failure) and "Showing the first 100 users." (a truncation notice).
   * The hint carried a fourth, "Staff couldn't be loaded — the list is unread,
   * not empty.", and is gone with them.
   *
   * ⚠️ What SURVIVES is the narrowing note, and it is not the same species: this
   * picker is narrowed to a user group the server ENFORCES, so naming somebody
   * outside it is a live 422 on `/companies`. It is a refusal, and it is also
   * what stops a one-name dropdown reading as a broken fetch. Do not sweep it.
   */
  const footnote = groupNote ?? undefined;

  return (
    <Field label={label} error={error} hint={groupNote ?? undefined}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={value}
          options={options}
          // The trigger's own resting text, and it must agree with the blank
          // row it stands for — they are the same choice seen closed and open.
          placeholder={loading ? 'Loading users…' : emptyLabel}
          emptyOptionLabel={emptyLabel}
          searchPlaceholder="Search staff…"
          loading={loading}
          footnote={footnote}
          // Not disabled on failure: the record's own holder is still injected
          // above, and locking the control would take away the one thing that
          // still works — keeping or clearing the person already assigned.
          disabled={loading}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={onChange}
        />
      )}
    </Field>
  );
}

/**
 * ⚠️ **A discriminated union, so `tsc` refuses the half-way state.** A
 * non-collapsible section takes no `open`, no `onToggle` and no `id` — it has
 * no panel for a button to control and no disclosure state to hold — and a
 * collapsible one must supply all three. Making `open`/`onToggle` merely
 * optional would let a caller pass `open` to a section that ignores it, which
 * is exactly the inert control this mode exists to avoid.
 */
type SectionProps = {
  title: string;
  summary: string;
  errorCount: number;
  children: ReactNode;
} & (
  | { collapsible?: true; id: SectionId; open: boolean; onToggle: () => void }
  | { collapsible: false; id?: never; open?: never; onToggle?: never }
);

/**
 * A disclosure built from a button and a region rather than `<details>`, so a
 * failed save can force it open without fighting the element's own state.
 * Collapsed content stays mounted — typing in a section then closing it must
 * not throw the input away.
 *
 * `collapsible={false}` is the same box, heading, summary and error badge with
 * the disclosure removed entirely: no chevron and no button, rather than a
 * control whose only outcome is nothing happening.
 */
function Section(props: SectionProps) {
  const { title, summary, errorCount, children } = props;
  const uid = useId();

  if (props.collapsible === false) {
    return (
      <section className="rounded-lg border border-border">
        <h3 className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
          {/*
           * An empty slot the width of `ChevronIcon`, so this heading lines up
           * with the collapsible sections it sits among rather than reading as
           * a different kind of block.
           */}
          <span aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium text-fg">{title}</span>
          <span className="hidden text-xs text-fg-muted sm:inline">{summary}</span>
          {errorCount > 0 && (
            <Badge tone="danger" className="ml-auto">
              {errorCount === 1 ? '1 error' : `${errorCount} errors`}
            </Badge>
          )}
        </h3>
        <div className="border-t border-border px-3 py-3">{children}</div>
      </section>
    );
  }

  const { id, open, onToggle } = props;
  const panelId = `${uid}-${id}`;

  return (
    <section className="rounded-lg border border-border">
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          className={cx(
            'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left',
            'transition-colors duration-150 ease-out motion-reduce:transition-none',
            'hover:bg-surface-hover',
            open && 'rounded-b-none',
          )}
        >
          <ChevronIcon open={open} />
          <span className="text-sm font-medium text-fg">{title}</span>
          <span className="hidden text-xs text-fg-muted sm:inline">{summary}</span>
          {errorCount > 0 && (
            <Badge tone="danger" className="ml-auto">
              {errorCount === 1 ? '1 error' : `${errorCount} errors`}
            </Badge>
          )}
        </button>
      </h3>
      <div id={panelId} hidden={!open} className="border-t border-border px-3 py-3">
        {children}
      </div>
    </section>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      {open ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}
